import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Modal, SUB_CONFIRM_LAYER, useConfirmDialog, useModalRequestClose } from '@safetyhub/ui';
import { adminApi, extractApiError } from '../lib/api';
import { useToastStore } from '../stores/toast';
import { ModalCancelButton } from '../components/ModalCancelButton';
import { useJobPolling, runJobRecovery } from '../hooks/useJobPolling';

type HazardKnowledgeSource = 'AI' | 'SIF' | 'EXTERNAL' | 'USER' | 'SYSTEM';

interface KnowledgeMeasureItem {
  id: string;
  content: string;
  category: string | null;
  confidence: number | null;
  usageCount: number;
  sortOrder: number;
}

interface RegulationLink {
  id: string;
  regulationId: string;
  type: string;
  code: string | null;
  article: string | null;
  title: string | null;
  summary: string | null;
  url: string | null;
  relevance: number | null;
}

interface Measure {
  id: string;
  industryType?: string | null; // 산업유형 (건설업, 제조업 등)
  category: string;
  subCategory: string | null;
  detailCategory?: string | null;
  hazardFactor?: string | null;
  riskSituation?: string | null;
  measures: KnowledgeMeasureItem[];
  source: HazardKnowledgeSource;
  usageCount: number;
  confidence: number | null;
  regulations: RegulationLink[];
  organizationId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface MeasureStats {
  total: number;
  regulationMatched: number;
  bySource: Record<string, number>;
  byCategory: Array<{
    category: string;
    count: number;
  }>;
  avgConfidence: number | null;
  totalUsage: number;
}

// 시트별 설정 보존용
interface PerSheetConfig {
  columnMapping: Record<string, string | number | null>;
  classificationColumns: string[];
  excludedRows: Set<number>;
  detectedHeaderRows: number[];
  detectedDataStartRow: number | undefined;
  addToClassification: boolean;
}

// Import 관련 타입
type ImportStep = 'select' | 'analyze' | 'mapping' | 'importing' | 'result';
type ImportMode = 'sif' | 'dynamic' | null;

interface SheetInfo {
  name: string;
  columns: string[];
  rowCount: number;
  sampleData: Record<string, unknown>[];
  rawPreview?: Array<{ row: number; data: unknown[] }>;
}

interface AIFieldMapping {
  column: string | null;
  confidence: number;
}

interface AIRecommendation {
  recommendedSheet: string;
  fieldMapping: Record<string, AIFieldMapping>;
  overallConfidence: number;
  reasoning: string;
}

interface AIImportableSheet {
  sheetName: string;
  description: string;
  headerRows: number[];
  dataStartRow: number;
  estimatedDataRows: number;
  fieldMapping: Record<string, { columnIndex: number; headerText: string; confidence: number }>;
  // AI가 유추한 산업유형
  inferredIndustryType?: {
    value: string;
    confidence: number;
    reasoning: string;
  };
  // AI가 유추한 카테고리 (다른 시트에서 추출)
  inferredCategory?: {
    value: string;
    confidence: number;
    reasoning: string;
    sourceSheet?: string;
  };
  // AI가 유추한 중분류/작업명 (문서 내용에서 추론)
  inferredSubCategory?: {
    value: string;
    confidence: number;
    reasoning: string;
  };
  // AI가 유추한 소분류/단위작업 (문서 전체가 하나의 단위작업에 해당할 때)
  inferredDetailCategory?: {
    value: string;
    confidence: number;
    reasoning: string;
  };
}

interface AIComprehensiveAnalysis {
  documentType: string;
  importableSheets: AIImportableSheet[];
  nonImportableSheets: Array<{ sheetName: string; reason: string }>;
  overallConfidence: number;
  reasoning: string;
}

interface AnalysisResult {
  fileName: string;
  sheets: SheetInfo[];
  suggestedMapping: Array<{
    sheetName: string;
    mapping: Record<string, string | number | null>;
    confidence: number;
  }>;
  aiRecommendation?: AIRecommendation;
  comprehensiveAnalysis?: AIComprehensiveAnalysis;
}

interface ImportResult {
  success: boolean;
  totalProcessed: number;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  details: Array<{
    sheet: string;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
  }>;
}

// 지식베이스 필드 정의 (SIF 필드와 1:1 대응)
// riskSituation은 검색용 복합 필드로 자동 생성되므로 매핑 UI에서 제외
// 핵심 필드는 모두 필수 (AI 유추 또는 사용자 입력 필요)
const KNOWLEDGE_FIELDS = [
  { key: 'industryType', label: '산업유형', required: true, description: '건설업, 제조업 등', canInfer: true },
  { key: 'category', label: '대분류/공종', required: true, description: '토공사, 철근콘크리트공사 등', canInfer: true },
  { key: 'subCategory', label: '중분류/작업명', required: true, description: '굴착작업, 거푸집작업 등', canInfer: true },
  { key: 'detailCategory', label: '소분류/단위작업', required: false, description: '자재반입, 조립 등', canInfer: false },
  { key: 'hazardFactor', label: '유해위험요인', required: true, description: '위험 상황 설명', canInfer: false },
  { key: 'measure', label: '감소대책', required: true, description: '안전 조치사항', canInfer: false },
];

const SOURCE_LABELS: Record<HazardKnowledgeSource, string> = {
  AI: 'AI 생성',
  SIF: 'SIF 데이터',
  EXTERNAL: '외부 데이터',
  USER: '수동 등록',
  SYSTEM: '시스템',
};

const SOURCE_COLORS: Record<HazardKnowledgeSource, string> = {
  AI: 'bg-blue-100 text-blue-700',
  SIF: 'bg-orange-100 text-orange-700',
  EXTERNAL: 'bg-purple-100 text-purple-700',
  USER: 'bg-green-100 text-green-700',
  SYSTEM: 'bg-gray-100 text-gray-700',
};

/**
 * 데이터 Import 창의 제목 줄 ✕ — base 헤더를 쓰지 않는 창이라 ✕가 본문에 있다. 닫기를 base의 닫기
 * 요청으로 수렴시켜 `isDirty` 확인을 거치게 한다(직접 `onClose`면 분석 결과가 확인 없이 버려진다).
 * `useModalRequestClose`는 Modal children **하위**에서만 값을 잡으므로 페이지 본문이 아니라 별도
 * 컴포넌트여야 한다(선례 `CompanyRoleFormModal`의 푸터).
 */
function ImportHeaderCloseButton({ onClose }: { onClose: () => void }) {
  const requestClose = useModalRequestClose();
  return (
    <button
      type="button"
      aria-label="닫기"
      onClick={requestClose ?? onClose}
      className="text-gray-500 hover:text-gray-700 text-2xl shrink-0 ml-3"
    >
      &times;
    </button>
  );
}

export default function MeasuresLibrary() {
  const showToast = useToastStore((state) => state.showToast);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirm, confirmDialog] = useConfirmDialog();

  // 목록 상태
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [stats, setStats] = useState<MeasureStats | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // 필터
  const [search, setSearch] = useState('');
  const [industryTypeFilter, setIndustryTypeFilter] = useState('');
  const [industryTypes, setIndustryTypes] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [sourceFilter, setSourceFilter] = useState<HazardKnowledgeSource | ''>('');
  const [sortBy, setSortBy] = useState<'usageCount' | 'confidence' | 'createdAt' | 'category'>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // 선택된 항목 — 「id → 라벨」 Map이다(Set이 아니다). 선택이 «페이지를 넘어» 살아남으므로
  // 삭제 확인 모달이 대상을 보일 때 현재 페이지의 `measures`(limit 20)에서는 찾을 수 없다.
  const [selectedItems, setSelectedItems] = useState<Map<string, string>>(new Map());

  // 일괄 삭제 확인 — native confirm이 아니다. 그 창은 «수»만 말할 수 있는데, 이 삭제는
  // `hazardKnowledge.deleteMany` hard delete이고 그 서비스에 감사 축이 없어
  // 「무엇을 지웠나」를 사후에 셀 방법이 0이다. 그래서 지우기 전에 보여 준다.
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // 모달
  const [detailModal, setDetailModal] = useState<Measure | null>(null);
  const [editModal, setEditModal] = useState<Measure | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  const resetPasswordRef = useRef<HTMLInputElement>(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [matchRegulationsModal, setMatchRegulationsModal] = useState(false);
  const [matchRegulationsLoading, setMatchRegulationsLoading] = useState(false);
  const [forceRematch, setForceRematch] = useState(false);
  const [matchProgress, setMatchProgress] = useState<{
    status: 'processing' | 'completed' | 'failed';
    processed: number;
    total: number;
    matched: number;
    skipped: number;
    avgSimilarity?: number;
    error?: string;
  } | null>(null);
  const [resetLegalBasesLoading, setResetLegalBasesLoading] = useState(false);
  const [legalBasesFilter, setLegalBasesFilter] = useState<'matched' | 'unmatched' | ''>('');

  // 편집 폼
  const [editForm, setEditForm] = useState({
    category: '',
    subCategory: '',
    hazardFactor: '',
    riskSituation: '',
    measureInputs: [''] as string[], // Array of measure strings for create
    source: 'USER' as HazardKnowledgeSource,
  });

  // Import 상태
  const [importStep, setImportStep] = useState<ImportStep>('select');
  const [importMode, setImportMode] = useState<ImportMode>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
  const [fileAnalysisMap, setFileAnalysisMap] = useState<Map<string, AnalysisResult>>(new Map());
  const [analyzeProgress, setAnalyzeProgress] = useState<{
    completed: number;
    total: number;
    activeFiles: string[];  // 현재 분석 중인 파일명들
  } | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState<string>('');
  const [columnMapping, setColumnMapping] = useState<Record<string, string | number | null>>({});
  const sourceType = 'EXTERNAL';
  const skipDuplicates = true;
  const [classificationColumns, setClassificationColumns] = useState<string[]>([]);
  // AI 종합 분석용 상태
  const [detectedHeaderRows, setDetectedHeaderRows] = useState<number[]>([]);
  const [detectedDataStartRow, setDetectedDataStartRow] = useState<number | undefined>(undefined);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [addToClassification, setAddToClassification] = useState(false);
  const [sheetConfigs, setSheetConfigs] = useState<Map<string, PerSheetConfig>>(new Map());
  const [multiSheetProgress, setMultiSheetProgress] = useState<{
    totalSheets: number;
    completedSheets: number;
    currentSheetName: string;
    sheetResults: Array<{ fileName: string; sheetName: string; created: number; updated: number; skipped: number; error?: string }>;
  } | null>(null);
  const cancelledRef = useRef(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // WorkClassification 드롭다운 옵션 (Cascading dropdown용)
  const [wcDropdownOptions, setWcDropdownOptions] = useState<{
    industryTypes: string[];
    categories: { value: string; industryType: string }[];
    subCategories: { value: string; category: string; industryType: string }[];
  } | null>(null);

  // 진행률 상태
  const [importProgress, setImportProgress] = useState<{
    percent: number;
    currentSheet: string;
    processed: number;
    total: number;
    created: number;
    updated: number;
    skipped: number;
    postProcess?: 'embedding' | null;
    embeddingProcessed?: number;
    embeddingTotal?: number;
  } | null>(null);
  // 임베딩 전용 폴링 ref (import 없이 단독 임베딩 시 — jobId 없는 특수 패턴)
  const embeddingPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const analyzeJobIdRef = useRef<string | null>(null);

  // 파생 값: 기존 코드와의 호환성 유지
  const selectedFile = selectedFiles[currentFileIndex] ?? null;
  const analysisResult = selectedFile ? fileAnalysisMap.get(selectedFile.name) ?? null : null;

  // 임베딩 단독 실행 시 폴링 (import 없이 embedding-status 엔드포인트 사용)
  const startEmbeddingOnlyPolling = useCallback(() => {
    if (embeddingPollingRef.current) return;
    embeddingPollingRef.current = setInterval(async () => {
      try {
        const res = await adminApi.getEmbeddingStatus();
        const d = res.data;
        if (!d || !d.knowledge) {
          // 임베딩 완료
          if (embeddingPollingRef.current) {
            clearInterval(embeddingPollingRef.current);
            embeddingPollingRef.current = null;
          }
          setImportModal(false);
          setImportStep('select');
          setImportProgress(null);
          loadData();
          loadStats();
          return;
        }
        setImportProgress(prev => ({
          percent: 100,
          currentSheet: prev?.currentSheet || '',
          processed: prev?.processed || 0,
          total: prev?.total || 0,
          created: prev?.created || 0,
          updated: prev?.updated || 0,
          skipped: prev?.skipped || 0,
          postProcess: 'embedding',
          embeddingProcessed: d.knowledgeProgress?.processed ?? 0,
          embeddingTotal: d.knowledgeProgress?.total ?? 0,
        }));
      } catch {
        // 네트워크 오류 시 폴링 계속
      }
    }, 1000);
  }, []);

  // ─── Job 폴링 훅 인스턴스 ─────────────────────────────────────
  const batchImportPolling = useJobPolling<any>({
    fetchProgress: (jobId) => adminApi.getBatchImportProgress(jobId),
    deleteJob: (jobId) => adminApi.deleteBatchImportJob(jobId),
    isComplete: (d) => d.status === 'completed',
    isFailed: (d) => d.status === 'failed',
    onProgress: (d) => {
      setMultiSheetProgress({
        totalSheets: d.totalSheets,
        completedSheets: d.completedSheets,
        currentSheetName: d.currentSheetName,
        sheetResults: d.sheetResults,
      });
      setImportProgress({
        percent: d.currentSheetPercent,
        currentSheet: d.currentSheetName,
        processed: d.currentSheetProcessed,
        total: d.currentSheetTotal,
        created: d.totalCreated,
        updated: d.totalUpdated,
        skipped: d.totalSkipped,
        postProcess: d.postProcess,
        embeddingProcessed: d.embeddingProcessed,
        embeddingTotal: d.embeddingTotal,
      });
    },
    onComplete: (d) => {
      setImportLoading(false);
      setMultiSheetProgress(null);
      if (d.result) setImportResult(d.result);
      setImportStep('result');
      loadStats();
      loadData();
    },
    onFailed: (d) => {
      setImportLoading(false);
      setMultiSheetProgress(null);
      showToast(d.error || '배치 Import 실패', 'error');
      setImportStep('select');
      setImportProgress(null);
    },
  });

  const singleImportPolling = useJobPolling<any>({
    fetchProgress: (jobId) => adminApi.getImportProgress(jobId),
    deleteJob: (jobId) => adminApi.deleteImportJob(jobId),
    isComplete: (d) => d.status === 'completed',
    isFailed: (d) => d.status === 'failed',
    onProgress: (d) => {
      setImportProgress({
        percent: d.percent,
        currentSheet: d.currentSheet,
        processed: d.processed,
        total: d.total,
        created: d.created,
        updated: d.updated,
        skipped: d.skipped,
        postProcess: d.postProcess,
        embeddingProcessed: d.embeddingProcessed,
        embeddingTotal: d.embeddingTotal,
      });
    },
    onComplete: (d) => {
      setImportLoading(false);
      if (d.result) setImportResult(d.result);
      setImportStep('result');
      loadStats();
      loadData();
    },
    onFailed: (d) => {
      setImportLoading(false);
      showToast(d.error || 'Import 실패', 'error');
      setImportStep('select');
      setImportProgress(null);
    },
  });

  const analyzePolling = useJobPolling<any>({
    fetchProgress: (jobId) => adminApi.getAnalyzeProgress(jobId),
    deleteJob: (jobId) => adminApi.deleteAnalyzeJob(jobId),
    isComplete: (d) => d.status === 'completed',
    isFailed: (d) => d.status === 'failed',
    onProgress: (d) => {
      setAnalyzeProgress({
        completed: d.completed,
        total: d.total,
        activeFiles: d.activeFiles,
      });
    },
    onComplete: async (d) => {
      await applyAnalyzeResults(d.results);
    },
    onFailed: (d) => {
      showToast(d.error || '파일 분석에 실패했습니다.', 'error');
      setImportStep('select');
      setImportLoading(false);
    },
  });

  const matchPolling = useJobPolling<any>({
    fetchProgress: (jobId) => adminApi.getMatchRegulationsProgress(jobId),
    deleteJob: (jobId) => adminApi.deleteMatchJob(jobId),
    isComplete: (d) => d.status === 'completed',
    isFailed: (d) => d.status === 'failed',
    onProgress: (d) => {
      setMatchProgress(d);
    },
    onComplete: (d) => {
      setMatchProgress(d);
      showToast(
        `법규 매칭 완료: ${d.total.toLocaleString()}건 중 ${d.matched.toLocaleString()}건 매칭됨`,
        'success',
      );
      loadData();
      loadStats();
      setMatchRegulationsLoading(false);
    },
    onFailed: (d) => {
      showToast(d.error || '매칭 실패', 'error');
      setMatchRegulationsLoading(false);
    },
  });

  // StrictMode 중복 recovery 방지 (dev 모드에서 effect가 2회 실행됨)
  const recoveryRanRef = useRef(false);

  // ─── 초기 로드 + 진행 중인 작업 복구 ──────────────────────────
  useEffect(() => {
    loadData();
    loadIndustryTypes();
    loadCategories();
    loadStats();
    loadWcDropdownOptions();

    if (recoveryRanRef.current) return;
    recoveryRanRef.current = true;

    (async () => {
      try {
        await runJobRecovery([
          {
            name: 'batch-import',
            fetchActive: () => adminApi.getActiveBatchImport(),
            isProcessing: (d) => d?.status === 'processing',
            onProcessing: (d) => {
              setImportModal(true);
              setImportMode('dynamic');
              setImportStep('importing');
              setImportLoading(true);
              setMultiSheetProgress({
                totalSheets: d.totalSheets,
                completedSheets: d.completedSheets,
                currentSheetName: d.currentSheetName,
                sheetResults: d.sheetResults,
              });
              setImportProgress({
                percent: d.currentSheetPercent,
                currentSheet: d.currentSheetName,
                processed: d.currentSheetProcessed,
                total: d.currentSheetTotal,
                created: d.totalCreated,
                updated: d.totalUpdated,
                skipped: d.totalSkipped,
                postProcess: d.postProcess,
                embeddingProcessed: d.embeddingProcessed,
                embeddingTotal: d.embeddingTotal,
              });
              batchImportPolling.start(d.jobId);
            },
            isFailed: (d) => d?.status === 'failed',
            onFailed: async (d) => {
              showToast(d.error || '네트워크 문제로 이전 작업이 중단되었습니다. 다시 시도해주세요.', 'error');
              await adminApi.deleteBatchImportJob(d.jobId).catch(() => {});
            },
            isCompleted: (d) => d?.status === 'completed' && d.result,
            onCompleted: (d) => {
              setImportModal(true);
              setImportMode('dynamic');
              setImportResult(d.result);
              setImportStep('result');
              adminApi.deleteBatchImportJob(d.jobId).catch(() => {});
            },
          },
          {
            name: 'single-import',
            fetchActive: () => adminApi.getActiveImport(),
            isProcessing: (d) => !!d && d.status === 'processing',
            onProcessing: (d) => {
              setImportModal(true);
              setImportStep('importing');
              setImportProgress({
                percent: d.percent,
                currentSheet: d.currentSheet,
                processed: d.processed,
                total: d.total,
                created: d.created,
                updated: d.updated,
                skipped: d.skipped,
                postProcess: d.postProcess,
                embeddingProcessed: d.embeddingProcessed,
                embeddingTotal: d.embeddingTotal,
              });
              singleImportPolling.start(d.jobId);
            },
            isFailed: (d) => !!d && d.status === 'failed',
            onFailed: async (d) => {
              showToast(d.error || '네트워크 문제로 이전 작업이 중단되었습니다. 다시 시도해주세요.', 'error');
              await adminApi.deleteImportJob(d.jobId).catch(() => {});
            },
          },
          {
            name: 'embedding',
            fetchActive: () => adminApi.getEmbeddingStatus(),
            isProcessing: (d) => !!d?.knowledge,
            onProcessing: (d) => {
              setImportModal(true);
              setImportStep('importing');
              setImportProgress({
                percent: 100,
                currentSheet: '',
                processed: 0,
                total: 0,
                created: 0,
                updated: 0,
                skipped: 0,
                postProcess: 'embedding',
                embeddingProcessed: d.knowledgeProgress?.processed ?? 0,
                embeddingTotal: d.knowledgeProgress?.total ?? 0,
              });
              startEmbeddingOnlyPolling();
            },
          },
          {
            name: 'analyze',
            fetchActive: () => adminApi.getActiveAnalyze(),
            isProcessing: (d) => d?.status === 'processing',
            onProcessing: (d) => {
              analyzeJobIdRef.current = d.jobId;
              setImportModal(true);
              setImportMode('dynamic');
              setImportStep('analyze');
              setImportLoading(true);
              setAnalyzeProgress({
                completed: d.completed,
                total: d.total,
                activeFiles: d.activeFiles,
              });
              analyzePolling.start(d.jobId);
            },
            isFailed: (d) => d?.status === 'failed',
            onFailed: async (d) => {
              showToast(d.error || '네트워크 문제로 이전 작업이 중단되었습니다. 다시 시도해주세요.', 'error');
              await adminApi.deleteAnalyzeJob(d.jobId).catch(() => {});
            },
            isCompleted: (d) => d?.status === 'completed' && d.results,
            onCompleted: async (d) => {
              analyzeJobIdRef.current = d.jobId;
              setImportModal(true);
              setImportMode('dynamic');
              await applyAnalyzeResults(d.results);
              adminApi.deleteAnalyzeJob(d.jobId).catch(() => {});
            },
          },
          {
            name: 'match',
            fetchActive: () => adminApi.getActiveMatchJob(),
            isProcessing: (d) => !!d && d.status === 'processing',
            onProcessing: (d) => {
              setMatchRegulationsModal(true);
              setMatchRegulationsLoading(true);
              setMatchProgress({
                status: d.status,
                processed: d.processed,
                total: d.total,
                matched: d.matched,
                skipped: d.skipped,
              });
              matchPolling.start(d.jobId);
            },
            isFailed: (d) => !!d && d.status === 'failed',
            onFailed: async (d) => {
              showToast(d.error || '네트워크 문제로 이전 작업이 중단되었습니다. 다시 시도해주세요.', 'error');
              await adminApi.deleteMatchJob(d.jobId).catch(() => {});
            },
          },
        ]);
      } catch {
        // ignore
      }
    })();

    return () => {
      batchImportPolling.stop();
      singleImportPolling.stop();
      analyzePolling.stop();
      matchPolling.stop();
      if (embeddingPollingRef.current) clearInterval(embeddingPollingRef.current);
    };
  }, []);

  useEffect(() => {
    loadData();
  }, [page, industryTypeFilter, categoryFilter, subCategoryFilter, sourceFilter, sortBy, sortOrder, legalBasesFilter]);

  /**
   * 멤버십 필터가 바뀌면 선택을 비운다 — 「다른 집합을 보게 됐다」이기 때문이다.
   *
   * ⚠️ deps는 위 조회 effect에서 **`page`·`sortBy`·`sortOrder`를 뺀 것**이다. 그 셋은 조회
   * 집합의 «멤버십»이 아니라 창·순서만 바꾸므로, 넣으면 「여러 페이지에 걸쳐 고르고 한 번에
   * 삭제」가 불가능해진다(그 기능을 잃지 않는 것이 이 처방을 교집합 방식과 가르는 지점이다).
   * ⚠️ `search`도 deps가 아니다 — 그 값은 입력마다 바뀌지만 조회는 submit에서만 돌므로,
   * 넣으면 목록이 그대로인데 타자만 쳐도 선택이 사라진다. 그 축은 `handleSearch`가 맡는다.
   * ⛔ 그 제외는 **「조회가 submit에서만 돈다」는 이 화면의 전제에 달려 있다** — 검색어가 조회 키에
   * 실려 **입력마다 재조회하는** 화면이면 전제가 반대이므로 `search`를 deps에 **넣어야** 한다
   * (목록이 실제로 갈리므로 선택을 들고 있으면 안 된다). 이 제외를 그런 화면에 옮겨 적지 말 것.
   */
  useEffect(() => {
    setSelectedItems(new Map());
  }, [industryTypeFilter, categoryFilter, subCategoryFilter, sourceFilter, legalBasesFilter]);

  useEffect(() => {
    setCategoryFilter('');
    setSubCategoryFilter('');
    loadCategories(industryTypeFilter || undefined);
  }, [industryTypeFilter]);

  useEffect(() => {
    setSubCategoryFilter('');
    loadSubCategories(categoryFilter || undefined);
  }, [categoryFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getMeasures({
        page,
        limit: 20,
        search: search || undefined,
        industryType: industryTypeFilter || undefined,
        category: categoryFilter || undefined,
        subCategory: subCategoryFilter || undefined,
        source: sourceFilter || undefined,
        sortBy,
        sortOrder,
        legalBasesStatus: legalBasesFilter || undefined,
      });

      if (response.data) {
        setMeasures(response.data.data);
        setTotalPages(response.data.totalPages);
        setTotal(response.data.total);
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await adminApi.getMeasureStats();
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadIndustryTypes = async () => {
    try {
      const response = await adminApi.getMeasureIndustryTypes();
      if (response.data) {
        setIndustryTypes(response.data);
      }
    } catch (error) {
      console.error('Failed to load industry types:', error);
    }
  };

  const loadCategories = async (industryType?: string) => {
    try {
      const response = await adminApi.getMeasureCategories(industryType);
      if (response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadSubCategories = async (category?: string) => {
    try {
      const response = await adminApi.getMeasureSubCategories(category || undefined);
      if (response.data) {
        setSubCategories(response.data);
      }
    } catch (error) {
      console.error('Failed to load subcategories:', error);
    }
  };

  const loadWcDropdownOptions = async () => {
    try {
      const response = await adminApi.getWorkClassificationDropdownOptions();
      if (response.data) {
        setWcDropdownOptions(response.data);
      }
    } catch (error) {
      console.error('Failed to load WorkClassification dropdown options:', error);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    // 검색 submit도 멤버십 필터다 — 위 리셋 effect의 deps에 `search`를 넣을 수 없어 여기서 비운다.
    setSelectedItems(new Map());
    loadData();
  };

  // 상세 모달의 「삭제」는 상세를 닫은 뒤 이것을 부르므로 확인은 root 층이다.
  const handleDelete = async (id: string) => {
    if (
      !(await confirm({
        title: '지식 삭제',
        body: '정말 삭제하시겠습니까?',
        confirmLabel: '삭제',
        danger: true,
      }))
    )
      return;

    try {
      await adminApi.deleteMeasure(id);
      showToast('삭제되었습니다.', 'success');
      // 지운 항목이 일괄 선택에 남으면 「N건 삭제」가 없는 id를 다시 보낸다(개수도 거짓이 된다).
      setSelectedItems((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
      loadData();
      loadStats();
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    setBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    const count = selectedItems.size;

    try {
      setBulkDeleteLoading(true);
      await adminApi.deleteMeasures(Array.from(selectedItems.keys()));
      showToast(`${count}개 항목이 삭제되었습니다.`, 'success');
      setSelectedItems(new Map());
      setBulkDeleteModal(false);
      loadData();
      loadStats();
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleCreate = async () => {
    const validMeasures = editForm.measureInputs.filter(m => m.trim());
    if (!editForm.category || validMeasures.length === 0) {
      showToast('카테고리와 최소 1개의 대책은 필수입니다.', 'error');
      return;
    }

    try {
      await adminApi.createMeasure({
        category: editForm.category,
        subCategory: editForm.subCategory || null,
        hazardFactor: editForm.hazardFactor || null,
        riskSituation: editForm.riskSituation || null,
        measures: validMeasures,
        source: editForm.source,
      });
      showToast('등록되었습니다.', 'success');
      setCreateModal(false);
      resetEditForm();
      loadData();
      loadStats();
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    }
  };

  const handleUpdate = async () => {
    if (!editModal) return;

    try {
      const validMeasures = editForm.measureInputs.filter(m => m.trim());

      // Compute measure changes
      const existingContents = editModal.measures.map(m => m.content);
      const newContents = validMeasures;

      // Find measures to add (in new but not in existing)
      const addMeasures = newContents.filter(c => !existingContents.includes(c));

      // Find measures to remove (in existing but not in new)
      const removeMeasureIds = editModal.measures
        .filter(m => !newContents.includes(m.content))
        .map(m => m.id);

      await adminApi.updateMeasure(editModal.id, {
        category: editForm.category,
        subCategory: editForm.subCategory || null,
        hazardFactor: editForm.hazardFactor || null,
        riskSituation: editForm.riskSituation || null,
        source: editForm.source,
        ...(addMeasures.length > 0 && { addMeasures }),
        ...(removeMeasureIds.length > 0 && { removeMeasureIds }),
      });
      showToast('수정되었습니다.', 'success');
      setEditModal(null);
      resetEditForm();
      loadData();
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    }
  };

  /**
   * 편집 폼 값 — 대상이 없으면 빈 생성 폼. 여는 경로(openEditModal·resetEditForm)와 미저장 닫기
   * 보호의 기준선(`isEditFormDirty`)이 **같은 식**을 쓰게 한 자리로 모은다(갈라지면 열자마자 dirty가 된다).
   */
  const buildEditForm = (measure: Measure | null): typeof editForm => {
    if (!measure) {
      return {
        category: '',
        subCategory: '',
        hazardFactor: '',
        riskSituation: '',
        measureInputs: [''],
        source: 'USER',
      };
    }
    return {
      category: measure.category,
      subCategory: measure.subCategory || '',
      hazardFactor: measure.hazardFactor || '',
      riskSituation: measure.riskSituation || '',
      measureInputs: measure.measures.length > 0
        ? measure.measures.map(m => m.content)
        : [''],
      source: measure.source,
    };
  };

  const resetEditForm = () => {
    setEditForm(buildEditForm(null));
  };

  const openEditModal = (measure: Measure) => {
    setEditModal(measure);
    setEditForm(buildEditForm(measure));
  };

  const closeEditFormModal = () => {
    setCreateModal(false);
    setEditModal(null);
    resetEditForm();
  };

  // 미저장 닫기 보호 — 기준선은 열린 대상(수정이면 그 지식, 생성이면 빈 폼). 고정 키 순서로 직렬화해 비교한다.
  const serializeEditForm = (f: typeof editForm) =>
    JSON.stringify([f.category, f.subCategory, f.hazardFactor, f.riskSituation, f.measureInputs, f.source]);
  const isEditFormDirty = serializeEditForm(editForm) !== serializeEditForm(buildEditForm(editModal));

  /**
   * 삭제 확인 모달에 보일 한 줄. 표의 식별 칸(산업/대분류 · 중분류 · 위험요인)을 그대로 잇고,
   * 위험요인이 비면 첫 대책 본문으로 떨어진다.
   */
  const measureLabel = (m: Measure): string =>
    [
      [m.industryType, m.category].filter(Boolean).join('/'),
      m.subCategory,
      m.hazardFactor || m.measures?.[0]?.content,
    ]
      .filter(Boolean)
      .join(' · ') || m.id;

  const toggleSelect = (m: Measure) => {
    const next = new Map(selectedItems);
    if (next.has(m.id)) {
      next.delete(m.id);
    } else {
      next.set(m.id, measureLabel(m));
    }
    setSelectedItems(next);
  };

  /**
   * 「이 페이지가 전부 선택됐나」를 «멤버십»으로 판정한다 — 수로 판정하면 안 된다.
   * 선택이 페이지를 넘어 살아남으므로 `size === measures.length`는 **다른 페이지에서 고른
   * 20개**를 「이 페이지 전체 선택됨」으로 읽고, 그 상태에서 누르면 선택이 통째로 비워진다.
   */
  const allOnPageSelected = measures.length > 0 && measures.every((m) => selectedItems.has(m.id));

  /**
   * 선택했지만 «현재 페이지에 없는» 개수. 선택 바가 이것을 말해 주지 않으면 운영자는
   * 「화면에 보이는 것만 지워진다」로 읽는다 — 그 오독이 되돌릴 수 없는 삭제로 이어진다.
   */
  const offPageSelectedCount = Array.from(selectedItems.keys()).filter(
    (id) => !measures.some((m) => m.id === id),
  ).length;

  /** 현재 «페이지»의 전체 선택/해제 — 다른 페이지에서 고른 것은 건드리지 않는다. */
  const toggleSelectAll = () => {
    const next = new Map(selectedItems);
    if (allOnPageSelected) {
      measures.forEach((m) => next.delete(m.id));
    } else {
      measures.forEach((m) => next.set(m.id, measureLabel(m)));
    }
    setSelectedItems(next);
  };

  // ========== Import 관련 함수 ==========

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const validExtensions = ['.xlsx', '.xls', '.xlsm'];
    const validFiles = files.filter(f => {
      const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
      return validExtensions.includes(ext);
    });
    if (validFiles.length === 0) {
      showToast('엑셀 파일(.xlsx, .xls, .xlsm)만 업로드 가능합니다.', 'error');
      return;
    }
    if (validFiles.length < files.length) {
      showToast(`${files.length - validFiles.length}개 파일이 지원하지 않는 형식이어서 제외되었습니다.`, 'info');
    }
    setSelectedFiles(validFiles);
    setCurrentFileIndex(0);
  };

  const handleSifImport = async () => {
    if (!selectedFile) return;

    setImportStep('importing');
    setImportLoading(true);

    try {
      const response = await adminApi.importSif(selectedFile);

      if (response.data?.jobId) {
        setImportProgress({ percent: 0, currentSheet: '', processed: 0, total: 0, created: 0, updated: 0, skipped: 0 });
        singleImportPolling.start(response.data.jobId);
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
      setImportStep('select');
      setImportLoading(false);
    }
  };

  // 시트별 매핑 빌드 헬퍼 (AI 분석 결과 기반)
  const buildMappingForSheet = async (compSheet: AIImportableSheet): Promise<Record<string, string | number | null>> => {
    const indexMapping: Record<string, string | number | null> = {};
    const canInferKeys = new Set(KNOWLEDGE_FIELDS.filter(f => f.canInfer).map(f => f.key));
    for (const [field, mapping] of Object.entries(compSheet.fieldMapping)) {
      // canInfer 필드는 칼럼 인덱스가 아닌 문자열 값으로만 설정 (분류 체계 드롭다운용)
      if (!canInferKeys.has(field)) {
        indexMapping[field] = mapping.columnIndex;
      }
    }
    if (compSheet.inferredIndustryType?.value) {
      indexMapping.industryType = `inferred:${compSheet.inferredIndustryType.value}`;
    }

    if (compSheet.inferredSubCategory?.value) {
      indexMapping.subCategory = `inferred:${compSheet.inferredSubCategory.value}`;
      const industryType = compSheet.inferredIndustryType?.value || '건설업';
      try {
        const lookupResult = await adminApi.lookupWorkClassification(
          industryType,
          compSheet.inferredSubCategory.value
        );
        if (lookupResult.data && lookupResult.data.length > 0) {
          indexMapping.category = lookupResult.data[0].category;
          indexMapping.industryType = lookupResult.data[0].industryType;
          indexMapping.subCategory = `inferred:${lookupResult.data[0].subCategory}`;
        } else {
          if (compSheet.inferredCategory?.value) {
            indexMapping.category = `inferred:${compSheet.inferredCategory.value}`;
          }
          if (compSheet.inferredIndustryType?.value) {
            indexMapping.industryType = `inferred:${compSheet.inferredIndustryType.value}`;
          }
        }
      } catch (error) {
        console.warn('WorkClassification lookup failed:', error);
        if (compSheet.inferredCategory?.value) {
          indexMapping.category = `inferred:${compSheet.inferredCategory.value}`;
        }
        if (compSheet.inferredIndustryType?.value) {
          indexMapping.industryType = `inferred:${compSheet.inferredIndustryType.value}`;
        }
      }
    } else if (compSheet.inferredCategory?.value) {
      indexMapping.category = `inferred:${compSheet.inferredCategory.value}`;
    }

    // inferredDetailCategory: 칼럼 매핑 영역에서는 자동 적용하지 않음
    // 서버에서 inferredDetailCategory fallback으로 처리

    return indexMapping;
  };

  /**
   * measure 칼럼이 비어있거나 "-"인 행을 자동 제외 Set 생성
   */
  const buildAutoExcludedRows = (
    compSheet: AIImportableSheet,
    sheetInfo: SheetInfo | undefined,
  ): Set<number> => {
    const excluded = new Set<number>();
    const measureMapping = compSheet.fieldMapping.measure;
    if (!measureMapping || !sheetInfo?.rawPreview) return excluded;

    const colIdx = measureMapping.columnIndex;
    for (const entry of sheetInfo.rawPreview) {
      if (entry.row < compSheet.dataStartRow) continue;
      const cell = entry.data[colIdx];
      const val = cell !== null && cell !== undefined ? String(cell).trim() : '';
      if (!val || val === '-') {
        excluded.add(entry.row);
      }
    }
    return excluded;
  };

  /**
   * 분석 결과(서버 results)를 fileAnalysisMap/sheetConfigs에 적용 후 mapping 단계로 전환
   */
  const applyAnalyzeResults = async (results: Record<string, any>) => {
    // 새로고침 후 File 객체가 사라진 경우 → placeholder File 생성
    let files = selectedFiles;
    if (files.length === 0 && Object.keys(results).length > 0) {
      const placeholders = Object.keys(results).map(name => new File([], name));
      setSelectedFiles(placeholders);
      files = placeholders;
    }

    // 서버 결과의 파일명과 브라우저 File.name 매핑 테이블 구축
    // (multer 파일명 인코딩 불일치 대응)
    const resultKeys = Object.keys(results).filter(k => results[k]);
    const nameMapping = new Map<string, string>(); // serverName → localName
    for (const serverName of resultKeys) {
      // 정확히 일치하는 파일 찾기
      const exactMatch = files.find(f => f.name === serverName);
      if (exactMatch) {
        nameMapping.set(serverName, exactMatch.name);
      } else {
        // 인코딩 불일치: 인덱스 기반으로 매칭 시도
        const serverIdx = resultKeys.indexOf(serverName);
        if (serverIdx < files.length) {
          nameMapping.set(serverName, files[serverIdx].name);
        }
      }
    }

    const newAnalysisMap = new Map<string, AnalysisResult>();
    const newSheetConfigs = new Map<string, PerSheetConfig>();

    for (const [serverName, data] of Object.entries(results)) {
      if (!data) continue;
      // 로컬 파일명으로 정규화하여 저장
      const localName = nameMapping.get(serverName) || serverName;
      newAnalysisMap.set(localName, data as AnalysisResult);
      const analysis = data as AnalysisResult;
      if (analysis.comprehensiveAnalysis) {
        for (const compSheet of analysis.comprehensiveAnalysis.importableSheets) {
          const mapping = await buildMappingForSheet(compSheet);
          const sheetInfo = analysis.sheets.find(s => s.name === compSheet.sheetName);
          newSheetConfigs.set(`${localName}::${compSheet.sheetName}`, {
            columnMapping: mapping,
            classificationColumns: [],
            excludedRows: buildAutoExcludedRows(compSheet, sheetInfo),
            detectedHeaderRows: compSheet.headerRows,
            detectedDataStartRow: compSheet.dataStartRow,
            addToClassification: false,
          });
        }
      }
    }

    setFileAnalysisMap(newAnalysisMap);
    setSheetConfigs(newSheetConfigs);
    setAnalyzeProgress(null);

    if (newAnalysisMap.size === 0) {
      showToast('분석에 성공한 파일이 없습니다.', 'error');
      setImportStep('select');
      setImportLoading(false);
      return;
    }

    // 첫 번째 성공한 파일의 첫 번째 시트 선택
    const firstFile = files.find(f => newAnalysisMap.has(f.name));
    if (firstFile) {
      const firstFileIdx = files.indexOf(firstFile);
      setCurrentFileIndex(firstFileIdx);
      const firstAnalysis = newAnalysisMap.get(firstFile.name)!;

      if (firstAnalysis.comprehensiveAnalysis && firstAnalysis.comprehensiveAnalysis.importableSheets.length > 0) {
        const bestSheet = firstAnalysis.comprehensiveAnalysis.importableSheets[0];
        setSelectedSheet(bestSheet.sheetName);
        const configKey = `${firstFile.name}::${bestSheet.sheetName}`;
        const bestConfig = newSheetConfigs.get(configKey);
        if (bestConfig) {
          setDetectedHeaderRows(bestConfig.detectedHeaderRows);
          setDetectedDataStartRow(bestConfig.detectedDataStartRow);
          setColumnMapping(bestConfig.columnMapping);
          setClassificationColumns(bestConfig.classificationColumns);
          setExcludedRows(bestConfig.excludedRows);
          setAddToClassification(bestConfig.addToClassification);
        }
      } else if (firstAnalysis.sheets.length > 0) {
        setSelectedSheet(firstAnalysis.sheets[0].name);
        setDetectedHeaderRows([]);
        setDetectedDataStartRow(undefined);
        const suggested = firstAnalysis.suggestedMapping.find(s => s.sheetName === firstAnalysis.sheets[0].name);
        if (suggested) {
          setColumnMapping(suggested.mapping);
        } else {
          setColumnMapping({});
        }
      }
    }

    const failCount = files.length - newAnalysisMap.size;
    if (failCount > 0) {
      showToast(`${failCount}개 파일 분석 실패, ${newAnalysisMap.size}개 성공`, 'info');
    }

    setImportStep('mapping');
    setImportLoading(false);
  };

  const analyzeFiles = async () => {
    if (selectedFiles.length === 0) return;

    setImportLoading(true);
    setImportStep('analyze');
    setAnalyzeProgress({ completed: 0, total: selectedFiles.length, activeFiles: [] });

    try {
      const res = await adminApi.analyzeBatch(selectedFiles);
      if (!res.data) {
        showToast('분석 요청 실패', 'error');
        setImportStep('select');
        setImportLoading(false);
        return;
      }
      const { jobId } = res.data;
      analyzeJobIdRef.current = jobId;
      analyzePolling.start(jobId);
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(`분석 요청 실패: ${message}`, 'error');
      setImportStep('select');
      setImportLoading(false);
    }
  };

  // 시트별 canImport 판정 헬퍼
  const getMissingRequiredFields = (config: PerSheetConfig): string[] => {
    return KNOWLEDGE_FIELDS.filter(f => f.required).filter(f => {
      const val = config.columnMapping[f.key];
      if (f.canInfer) {
        if (typeof val === 'number') return val < 0;
        if (typeof val !== 'string') return true;
        const stripped = val.startsWith('inferred:') ? val.slice(9) : val.startsWith('custom:') ? val.slice(7) : val;
        return stripped.trim() === '';
      } else {
        return !(typeof val === 'number' && val >= 0);
      }
    }).map(f => f.label);
  };

  // 칼럼 매핑 필드(canInfer: false) 중 매핑 안 된 것
  const getMissingColumnFields = (config: PerSheetConfig): string[] => {
    return KNOWLEDGE_FIELDS.filter(f => !f.canInfer).filter(f => {
      const val = config.columnMapping[f.key];
      return !(typeof val === 'number' && val >= 0);
    }).map(f => f.label);
  };

  const checkSheetCanImport = (config: PerSheetConfig): boolean => {
    return getMissingRequiredFields(config).length === 0;
  };

  // 시트별 유효 행 수 계산 헬퍼 (analysisData 파라미터 추가로 다중 파일 지원)
  const getValidRowCount = (sheetName: string, config: PerSheetConfig, analysisData?: AnalysisResult | null): number => {
    const ar = analysisData ?? analysisResult;
    const sheet = ar?.sheets.find(s => s.name === sheetName);
    if (!sheet?.rawPreview || sheet.rawPreview.length === 0) return 0;

    const compSheet = ar?.comprehensiveAnalysis?.importableSheets.find(
      s => s.sheetName === sheetName
    );

    const requiredFieldKeys = KNOWLEDGE_FIELDS.filter(f => !f.canInfer && f.required).map(f => f.key);
    const mappedIndices = requiredFieldKeys
      .map(key => {
        const val = config.columnMapping[key];
        if (typeof val === 'number' && val >= 0) return val;
        return -1;
      })
      .filter(idx => idx >= 0);

    if (mappedIndices.length < requiredFieldKeys.length) return 0;

    const dataStartRow = config.detectedDataStartRow ?? compSheet?.dataStartRow ?? (sheet.rawPreview[0]?.row ?? 0) + 1;

    return sheet.rawPreview
      .filter(r => r.row > dataStartRow - 1)
      .filter(r => !config.excludedRows.has(r.row))
      .filter(r => {
        const rowData = r.data as unknown[];
        return mappedIndices.every(idx => {
          const val = rowData[idx];
          return val !== undefined && val !== null && val !== '';
        });
      }).length;
  };

  // 전체 파일 × 시트 일괄 Import
  const handleImportAll = async () => {
    if (selectedFiles.length === 0 || fileAnalysisMap.size === 0) return;

    // 현재 시트 config 저장
    saveCurrentSheetConfig();

    // 현재 시트의 최신 config (setState 비동기 대비)
    const currentConfigKey = selectedFile ? getSheetConfigKey(selectedFile.name, selectedSheet) : '';
    const currentConfig: PerSheetConfig = {
      columnMapping: { ...columnMapping },
      classificationColumns: [...classificationColumns],
      excludedRows: new Set(excludedRows),
      detectedHeaderRows: [...detectedHeaderRows],
      detectedDataStartRow,
      addToClassification,
    };

    // 모든 파일 × 시트에서 Import 가능한 항목 수집
    const importableEntries: Array<{ file: File; sheetName: string; config: PerSheetConfig; analysisData: AnalysisResult }> = [];

    for (const file of selectedFiles) {
      const analysis = fileAnalysisMap.get(file.name);
      if (!analysis) continue;

      for (const sheet of analysis.sheets) {
        const key = getSheetConfigKey(file.name, sheet.name);
        // 현재 보고 있는 시트면 최신 config 사용
        const config = key === currentConfigKey ? currentConfig : sheetConfigs.get(key);
        if (!config) continue;
        if (!checkSheetCanImport(config) || getMissingColumnFields(config).length > 0) continue;
        const validCount = getValidRowCount(sheet.name, config, analysis);
        if (validCount <= 0) continue;
        importableEntries.push({ file, sheetName: sheet.name, config, analysisData: analysis });
      }

      // 현재 시트가 sheetConfigs에 없는 경우 처리
      if (file === selectedFile && !sheetConfigs.has(currentConfigKey) && selectedSheet) {
        if (checkSheetCanImport(currentConfig) && getMissingColumnFields(currentConfig).length === 0) {
          const validCount = getValidRowCount(selectedSheet, currentConfig, analysis);
          if (validCount > 0) {
            importableEntries.push({ file, sheetName: selectedSheet, config: currentConfig, analysisData: analysis });
          }
        }
      }
    }

    if (importableEntries.length === 0) {
      showToast('Import 가능한 시트가 없습니다.', 'error');
      return;
    }

    // extractValueAndSource 헬퍼
    const extractValueAndSource = (value: unknown): { value: string | undefined; source: 'AI' | 'USER' | 'DB' | undefined } => {
      if (typeof value === 'string') {
        if (value.startsWith('inferred:')) {
          const v = value.slice(9).trim();
          return { value: v || undefined, source: v ? 'AI' : undefined };
        }
        if (value.startsWith('custom:')) {
          const v = value.slice(7).trim();
          return { value: v || undefined, source: v ? 'USER' : undefined };
        }
        if (value.trim()) {
          return { value: value.trim(), source: 'DB' };
        }
      }
      return { value: undefined, source: undefined };
    };

    // 배치 config 빌드
    const sheets = importableEntries.map((entry) => {
      const cm = entry.config.columnMapping;
      const industryTypeInfo = extractValueAndSource(cm.industryType);
      const categoryInfo = extractValueAndSource(cm.category);
      const subCategoryInfo = extractValueAndSource(cm.subCategory);
      const detailCategoryInfo = extractValueAndSource(cm.detailCategory);
      const classificationSources: { industryType?: 'AI' | 'USER' | 'DB'; category?: 'AI' | 'USER' | 'DB'; subCategory?: 'AI' | 'USER' | 'DB'; detailCategory?: 'AI' | 'USER' | 'DB' } = {};
      if (industryTypeInfo.source) classificationSources.industryType = industryTypeInfo.source;
      if (categoryInfo.source) classificationSources.category = categoryInfo.source;
      if (subCategoryInfo.source) classificationSources.subCategory = subCategoryInfo.source;
      if (detailCategoryInfo.source) classificationSources.detailCategory = detailCategoryInfo.source;

      return {
        fileName: entry.file.name,
        sheetName: entry.sheetName,
        mapping: cm,
        classificationColumns: undefined, // 서버에서 항상 모든 원본 데이터 보존
        headerRows: entry.config.detectedHeaderRows.length > 0 ? entry.config.detectedHeaderRows : undefined,
        dataStartRow: entry.config.detectedDataStartRow,
        inferredIndustryType: industryTypeInfo.value,
        inferredCategory: categoryInfo.value,
        inferredSubCategory: subCategoryInfo.value,
        inferredDetailCategory: detailCategoryInfo.value,
        classificationSources: Object.keys(classificationSources).length > 0 ? classificationSources : undefined,
        excludedRows: entry.config.excludedRows.size > 0 ? Array.from(entry.config.excludedRows) : undefined,
        addToClassification: entry.config.addToClassification || undefined,
      };
    });

    setImportStep('importing');
    setImportLoading(true);

    setMultiSheetProgress({
      totalSheets: importableEntries.length,
      completedSheets: 0,
      currentSheetName: importableEntries[0].sheetName,
      sheetResults: [],
    });
    setImportProgress({ percent: 0, currentSheet: '', processed: 0, total: 0, created: 0, updated: 0, skipped: 0 });

    try {
      const response = await adminApi.startBatchImport({
        analyzeJobId: analyzeJobIdRef.current!,
        sourceType,
        skipDuplicates,
        sheets,
      });

      if (response.data?.jobId) {
        batchImportPolling.start(response.data.jobId);
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
      setImportStep('select');
      setImportLoading(false);
      setMultiSheetProgress(null);
      setImportProgress(null);
    }
  };

  const handleMappingChange = (field: string, column: string | number | null) => {
    setColumnMapping((prev) => ({
      ...prev,
      [field]: column,
    }));
  };

  // 현재 시트 config를 sheetConfigs에 저장 (복합키: fileName::sheetName)
  const getSheetConfigKey = (fileName: string, sheetName: string) => `${fileName}::${sheetName}`;
  const saveCurrentSheetConfig = () => {
    if (!selectedSheet || !selectedFile) return;
    const key = getSheetConfigKey(selectedFile.name, selectedSheet);
    setSheetConfigs((prev) => {
      const next = new Map(prev);
      next.set(key, {
        columnMapping: { ...columnMapping },
        classificationColumns: [...classificationColumns],
        excludedRows: new Set(excludedRows),
        detectedHeaderRows: [...detectedHeaderRows],
        detectedDataStartRow,
        addToClassification,
      });
      return next;
    });
  };

  const handleSheetChange = async (sheetName: string) => {
    // 1. 현재 시트 config 저장
    saveCurrentSheetConfig();

    // 2. 대상 시트로 전환
    setSelectedSheet(sheetName);

    // 3. 대상 시트 config 복원 시도 (복합키)
    const key = selectedFile ? getSheetConfigKey(selectedFile.name, sheetName) : sheetName;
    const savedConfig = sheetConfigs.get(key);
    if (savedConfig) {
      setColumnMapping(savedConfig.columnMapping);
      setClassificationColumns(savedConfig.classificationColumns);
      setExcludedRows(savedConfig.excludedRows);
      setDetectedHeaderRows(savedConfig.detectedHeaderRows);
      setDetectedDataStartRow(savedConfig.detectedDataStartRow);
      setAddToClassification(savedConfig.addToClassification);
      return;
    }

    // 4. 저장된 config이 없으면 AI 분석 결과로 새로 빌드
    setClassificationColumns([]);
    setExcludedRows(new Set());

    const compSheet = analysisResult?.comprehensiveAnalysis?.importableSheets.find(
      (s) => s.sheetName === sheetName
    );

    if (compSheet) {
      const mapping = await buildMappingForSheet(compSheet);
      const sheetInfo = analysisResult?.sheets.find(s => s.name === sheetName);
      const autoExcluded = buildAutoExcludedRows(compSheet, sheetInfo);
      setDetectedHeaderRows(compSheet.headerRows);
      setDetectedDataStartRow(compSheet.dataStartRow);
      setColumnMapping(mapping);
      setExcludedRows(autoExcluded);

      // 새로 빌드한 config도 sheetConfigs에 저장 (복합키)
      setSheetConfigs((prev) => {
        const next = new Map(prev);
        next.set(key, {
          columnMapping: mapping,
          classificationColumns: [],
          excludedRows: autoExcluded,
          detectedHeaderRows: compSheet.headerRows,
          detectedDataStartRow: compSheet.dataStartRow,
          addToClassification: false,
        });
        return next;
      });
      return;
    }

    // 레거시 방식: 키워드 분석 결과 사용
    setDetectedHeaderRows([]);
    setDetectedDataStartRow(undefined);
    const suggested = analysisResult?.suggestedMapping.find((s) => s.sheetName === sheetName);
    if (suggested) {
      setColumnMapping(suggested.mapping);
    } else {
      setColumnMapping({});
    }
  };

  // 파일 탭 전환
  const handleFileChange = (fileIndex: number) => {
    if (fileIndex === currentFileIndex) return;

    // 1. 현재 시트 config 저장
    saveCurrentSheetConfig();

    // 2. 대상 파일로 전환
    setCurrentFileIndex(fileIndex);

    // 3. 대상 파일의 첫 번째(또는 마지막 선택) 시트 복원
    const targetFile = selectedFiles[fileIndex];
    const targetAnalysis = fileAnalysisMap.get(targetFile.name);
    if (!targetAnalysis) return;

    // 대상 파일의 시트 중 녹색(완벽 매핑) 시트 우선, 없으면 노란색(후보) 시트 선택
    let greenSheet = '';
    let candidateSheet = '';
    let firstConfigSheet = '';
    for (const sheet of targetAnalysis.sheets) {
      const key = getSheetConfigKey(targetFile.name, sheet.name);
      const config = sheetConfigs.get(key);
      if (config) {
        if (!firstConfigSheet) firstConfigSheet = sheet.name;
        if (!greenSheet && checkSheetCanImport(config) && getMissingColumnFields(config).length === 0 && getValidRowCount(sheet.name, config, targetAnalysis) > 0) {
          greenSheet = sheet.name;
        } else if (!candidateSheet && checkSheetCanImport(config)) {
          candidateSheet = sheet.name;
        }
      }
    }
    const targetSheetName = greenSheet || candidateSheet || firstConfigSheet || (targetAnalysis.sheets[0]?.name ?? '');

    setSelectedSheet(targetSheetName);

    // 해당 시트의 config 복원
    const configKey = getSheetConfigKey(targetFile.name, targetSheetName);
    const savedConfig = sheetConfigs.get(configKey);
    if (savedConfig) {
      setColumnMapping(savedConfig.columnMapping);
      setClassificationColumns(savedConfig.classificationColumns);
      setExcludedRows(savedConfig.excludedRows);
      setDetectedHeaderRows(savedConfig.detectedHeaderRows);
      setDetectedDataStartRow(savedConfig.detectedDataStartRow);
      setAddToClassification(savedConfig.addToClassification);
    } else {
      // config 없으면 초기화
      setColumnMapping({});
      setClassificationColumns([]);
      setExcludedRows(new Set());
      setDetectedHeaderRows([]);
      setDetectedDataStartRow(undefined);
      setAddToClassification(false);
    }
  };

  const resetImport = () => {
    batchImportPolling.stop();
    singleImportPolling.stop();
    analyzePolling.stop();
    if (embeddingPollingRef.current) {
      clearInterval(embeddingPollingRef.current);
      embeddingPollingRef.current = null;
    }
    analyzeJobIdRef.current = null;
    setImportStep('select');
    setImportMode(null);
    setSelectedFiles([]);
    setCurrentFileIndex(0);
    setFileAnalysisMap(new Map());
    setAnalyzeProgress(null);
    setSelectedSheet('');
    setColumnMapping({});
    setClassificationColumns([]);
    setImportResult(null);
    setImportProgress(null);
    setDetectedHeaderRows([]);
    setDetectedDataStartRow(undefined);
    setExcludedRows(new Set());
    setSheetConfigs(new Map());
    setMultiSheetProgress(null);
    cancelledRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 미저장 닫기 보호(사용자 결정 2026-10-31) — 파일 고르기만 했으면 다시 고르면 되지만, **AI 분석 결과가
  // 생겼거나**(닫으면 분석을 다시 돌려야 한다) 매핑에 «직접 입력»(`custom:` 접두)이 있으면 확인을 거친다.
  // 결과 단계는 이미 반영이 끝나 잃을 것이 없다.
  const isImportDirty =
    importStep !== 'result' &&
    (fileAnalysisMap.size > 0 ||
      Object.values(columnMapping).some((v) => typeof v === 'string' && v.startsWith('custom:')));

  const closeImportModal = () => {
    if (importStep === 'importing' || importStep === 'analyze') return; // 진행 중 닫기 방지
    analyzePolling.stop();
    cancelledRef.current = true;
    setImportModal(false);
    resetImport();
  };

  const handleResetKnowledgeBase = async () => {
    if (!resetPassword) {
      showToast('비밀번호를 입력해주세요.', 'error');
      return;
    }

    setResetLoading(true);
    try {
      const response = await adminApi.resetKnowledgeBase(resetPassword);
      if (response.data) {
        showToast(
          `지식베이스가 초기화되었습니다. (지식 ${response.data.deletedKnowledge.toLocaleString()}건, 대책 ${response.data.deletedMeasures.toLocaleString()}건 삭제)`,
          'success',
        );
        setResetModal(false);
        setResetPassword('');
        loadData();
        loadStats();
        loadIndustryTypes();
        loadCategories();
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const handleMatchRegulations = async () => {
    setMatchRegulationsLoading(true);
    setMatchProgress(null);
    try {
      const response = await adminApi.matchRegulations({ forceRematch });
      if (!response.success) {
        showToast((response.error as any)?.message || '매칭 실행 실패', 'error');
        setMatchRegulationsLoading(false);
        return;
      }
      if (!response.data?.jobId) {
        setMatchRegulationsLoading(false);
        return;
      }

      const jobId = response.data.jobId;
      setMatchProgress({ status: 'processing', processed: 0, total: 0, matched: 0, skipped: 0 });
      matchPolling.start(jobId);
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
      setMatchRegulationsLoading(false);
    }
  };

  const handleResetLegalBases = async () => {
    setResetLegalBasesLoading(true);
    try {
      const response = await adminApi.resetLegalBases();
      if (response.data) {
        showToast(
          `법규 데이터 초기화 완료: ${response.data.updated.toLocaleString()}건`,
          'success',
        );
        loadData();
        loadStats();
      }
    } catch (error) {
      const { message } = extractApiError(error);
      showToast(message, 'error');
    } finally {
      setResetLegalBasesLoading(false);
    }
  };

  const currentSheetInfo = analysisResult?.sheets.find((s) => s.name === selectedSheet);

  // 종합 분석 시 현재 선택된 시트의 AI 분석 정보
  const currentCompSheet = analysisResult?.comprehensiveAnalysis?.importableSheets.find(
    (s) => s.sheetName === selectedSheet
  );

  // 칼럼 옵션 및 헤더 행 번호 계산
  const getColumnOptionsAndHeaderRow = (): { options: Array<{ index: number; name: string }>; headerRowNumber: number } => {
    if (currentSheetInfo?.rawPreview && currentSheetInfo.rawPreview.length > 0) {
      // 1. compSheet가 있고 headerRows가 있으면 해당 행 사용
      if (currentCompSheet && detectedHeaderRows.length > 0) {
        const headerRowsData = detectedHeaderRows
          .map((rowIdx) => currentSheetInfo.rawPreview?.find((r) => r.row === rowIdx)?.data)
          .filter(Boolean) as unknown[][];

        if (headerRowsData.length > 0) {
          const maxCols = Math.max(...headerRowsData.map((r) => r.length));
          const options: Array<{ index: number; name: string }> = [];

          for (let colIdx = 0; colIdx < maxCols; colIdx++) {
            // 여러 헤더 행 중 가장 마지막(가장 구체적인) 값 사용
            let colName = '';
            for (let i = headerRowsData.length - 1; i >= 0; i--) {
              const val = headerRowsData[i]?.[colIdx];
              if (val !== undefined && val !== null && String(val).trim()) {
                colName = String(val).trim();
                break;
              }
            }
            if (colName) {
              options.push({ index: colIdx, name: colName });
            }
          }
          // 마지막 헤더 행의 row 번호
          const lastHeaderRow = Math.max(...detectedHeaderRows);
          return { options, headerRowNumber: lastHeaderRow };
        }
      }

      // 2. compSheet가 없거나 headerRows가 없으면 rawPreview에서 텍스트가 많은 행을 헤더로 간주
      // 휴리스틱: 숫자보다 텍스트가 많은 첫 번째 행을 헤더로 사용
      let headerRowIdx = 0;
      for (let i = 0; i < Math.min(currentSheetInfo.rawPreview.length, 5); i++) {
        const rowData = currentSheetInfo.rawPreview[i]?.data as unknown[];
        if (!rowData) continue;

        // 텍스트(숫자가 아닌) 셀의 비율 계산
        const nonEmptyCells = rowData.filter((v) => v !== undefined && v !== null && String(v).trim());
        const textCells = nonEmptyCells.filter((v) => isNaN(Number(v)));
        const textRatio = nonEmptyCells.length > 0 ? textCells.length / nonEmptyCells.length : 0;

        // 50% 이상이 텍스트이고, 최소 2개 이상의 텍스트 셀이 있으면 헤더로 간주
        if (textRatio >= 0.5 && textCells.length >= 2) {
          headerRowIdx = i;
          break;
        }
      }

      const headerRow = currentSheetInfo.rawPreview[headerRowIdx];
      if (headerRow && headerRow.data) {
        const rowData = headerRow.data as unknown[];
        const options: Array<{ index: number; name: string }> = [];
        for (let colIdx = 0; colIdx < rowData.length; colIdx++) {
          const val = rowData[colIdx];
          const colName = val !== undefined && val !== null && String(val).trim()
            ? String(val).trim()
            : `칼럼${colIdx + 1}`;
          options.push({ index: colIdx, name: colName });
        }
        return { options, headerRowNumber: headerRow.row };
      }
    }

    // 레거시: 기존 columns 배열 사용
    return {
      options: (currentSheetInfo?.columns || []).map((name, index) => ({ index, name })),
      headerRowNumber: 0,
    };
  };

  const { options: columnOptions, headerRowNumber: detectedHeaderRowNumber } = getColumnOptionsAndHeaderRow();

  const getSourceBadge = (source: HazardKnowledgeSource) => {
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${SOURCE_COLORS[source]}`}>
        {SOURCE_LABELS[source]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">위험 대응 지식베이스</h1>
        <div className="flex gap-2">
          <Button variant="danger" onClick={() => setResetModal(true)}>
            초기화
          </Button>
          <Button tone="violet" onClick={() => setImportModal(true)}>
            데이터 Import
          </Button>
          <Button variant="success" onClick={() => setMatchRegulationsModal(true)}>
            법규 매칭
          </Button>
          <Button variant="primary" onClick={() => setCreateModal(true)}>
            + 수동 등록
          </Button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="space-y-3">
          {/* 출처별 통계 */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">전체</div>
              <div className="text-2xl font-bold">{stats.total.toLocaleString()}</div>
            </Card>
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">SIF 데이터</div>
              <div className="text-2xl font-bold text-orange-600">
                {(stats.bySource.SIF || 0).toLocaleString()}
              </div>
            </Card>
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">외부 데이터</div>
              <div className="text-2xl font-bold text-purple-600">
                {(stats.bySource.EXTERNAL || 0).toLocaleString()}
              </div>
            </Card>
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">수동 등록</div>
              <div className="text-2xl font-bold text-green-600">
                {(stats.bySource.USER || 0).toLocaleString()}
              </div>
            </Card>
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">총 사용 횟수</div>
              <div className="text-2xl font-bold text-blue-600">
                {stats.totalUsage.toLocaleString()}
              </div>
            </Card>
            <Card radius="lg" elevation="none">
              <div className="text-sm text-gray-500">법규 매칭</div>
              <div className="text-2xl font-bold text-teal-600">
                {stats.regulationMatched.toLocaleString()}
                <span className="text-sm font-normal text-gray-400 ml-1">
                  / {stats.total.toLocaleString()}
                </span>
              </div>
            </Card>
          </div>

          {/* 산업별 통계 */}
          {stats.byCategory.length > 0 && (
            <Card radius="lg" elevation="none">
              <div className="text-sm font-medium text-gray-500 mb-3">산업별 분포</div>
              <div className="flex gap-4 flex-wrap">
                {stats.byCategory.map((cat) => {
                  const percent = stats.total > 0 ? (cat.count / stats.total) * 100 : 0;
                  return (
                    <button
                      key={cat.category}
                      onClick={() => {
                        setCategoryFilter(cat.category === categoryFilter ? '' : cat.category);
                        setPage(1);
                      }}
                      className={`flex items-center gap-3 px-4 py-2 rounded-control border transition-colors ${
                        categoryFilter === cat.category
                          ? 'border-brand-500 bg-brand-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-left">
                        <div className="text-sm font-medium text-gray-900">{cat.category}</div>
                        <div className="text-xs text-gray-500">
                          {cat.count.toLocaleString()}건 ({percent.toFixed(1)}%)
                        </div>
                      </div>
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-brand-500 h-2 rounded-full"
                          style={{ width: `${Math.min(percent, 100)}%` }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* 필터 */}
      <Card radius="lg" elevation="none">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">검색</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="대책 내용, 카테고리 검색..."
              className="w-full px-3 py-2 border rounded-control"
            />
          </div>
          <div className="w-36">
            <label className="block text-sm font-medium text-gray-700 mb-1">산업유형</label>
            <select
              value={industryTypeFilter}
              onChange={(e) => {
                setIndustryTypeFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="">전체</option>
              {industryTypes.map((it) => (
                <option key={it} value={it}>
                  {it}
                </option>
              ))}
            </select>
          </div>
          <div className="w-40">
            <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="">전체</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="w-44">
            <label className="block text-sm font-medium text-gray-700 mb-1">중분류</label>
            <select
              value={subCategoryFilter}
              onChange={(e) => {
                setSubCategoryFilter(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
              disabled={subCategories.length === 0}
            >
              <option value="">전체</option>
              {subCategories.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">출처</label>
            <select
              value={sourceFilter}
              onChange={(e) => {
                setSourceFilter(e.target.value as HazardKnowledgeSource | '');
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="">전체</option>
              <option value="SIF">SIF 데이터</option>
              <option value="EXTERNAL">외부 데이터</option>
              <option value="USER">수동 등록</option>
              <option value="SYSTEM">시스템</option>
            </select>
          </div>
          <div className="w-32">
            <label className="block text-sm font-medium text-gray-700 mb-1">정렬</label>
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as typeof sortBy);
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="createdAt">생성일</option>
              <option value="usageCount">사용 횟수</option>
              <option value="confidence">신뢰도</option>
              <option value="category">카테고리</option>
            </select>
          </div>
          <div className="w-24">
            <label className="block text-sm font-medium text-gray-700 mb-1">순서</label>
            <select
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as 'asc' | 'desc');
                setPage(1);
              }}
              className="w-full px-3 py-2 border rounded-control"
            >
              <option value="desc">내림차순</option>
              <option value="asc">오름차순</option>
            </select>
          </div>
          <Button type="submit" variant="primary">
            검색
          </Button>
        </form>
        {legalBasesFilter && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-gray-500">활성 필터:</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
              {legalBasesFilter === 'unmatched' ? '미매칭 항목' : '매칭 완료 항목'}
              <button
                onClick={() => {
                  setLegalBasesFilter('');
                  setPage(1);
                }}
                className="ml-1 text-orange-500 hover:text-orange-700"
              >
                &times;
              </button>
            </span>
          </div>
        )}
      </Card>

      {/* 선택 액션 */}
      {selectedItems.size > 0 && (
        <div className="bg-brand-50 border border-brand-200 rounded-card p-3 flex items-center justify-between">
          <span className="text-brand-700">
            {selectedItems.size}개 선택됨
            {offPageSelectedCount > 0 && (
              <span className="ml-2 text-sm text-brand-600">
                (이 페이지에 없는 {offPageSelectedCount}개 포함)
              </span>
            )}
          </span>
          <Button variant="danger" size="sm" onClick={handleBulkDelete}>
            일괄 삭제
          </Button>
        </div>
      )}

      {/* 테이블 */}
      <Card padding="none" radius="lg" elevation="none" className="overflow-x-auto">
        <table className="min-w-[1400px] w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={toggleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[140px]">
                산업/대분류
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[120px]">
                중분류
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[120px]">
                소분류/고위험작업
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap min-w-[140px]">
                위험요인
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[280px]">
                대책
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-16">
                법규
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-20">
                출처
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap w-12">
                사용
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            ) : measures.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  데이터가 없습니다. Import 또는 수동 등록을 통해 추가해주세요.
                </td>
              </tr>
            ) : (
              measures.map((m) => (
                <tr
                  key={m.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setDetailModal(m)}
                >
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedItems.has(m.id)}
                      onChange={() => toggleSelect(m)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 whitespace-nowrap min-w-[140px] max-w-[200px] truncate" title={`${m.industryType || ''}${m.industryType && m.category ? ' / ' : ''}${m.category}`}>
                    {m.industryType && <span className="text-brand-600">{m.industryType}</span>}
                    {m.industryType && m.category && <span className="text-gray-400 mx-1">/</span>}
                    {m.category}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap min-w-[120px] max-w-[180px] truncate" title={m.subCategory || ''}>
                    {m.subCategory || <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap min-w-[120px] max-w-[180px] truncate" title={m.detailCategory || ''}>
                    {m.detailCategory || <span className="text-gray-300">-</span>}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 max-w-[180px]">
                    <div className="line-clamp-2" title={m.hazardFactor || ''}>
                      {m.hazardFactor || <span className="text-gray-300">-</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 min-w-[280px]">
                    <div className="text-sm text-gray-900 line-clamp-2" title={m.measures?.[0]?.content || ''}>
                      {m.measures?.[0]?.content || '-'}
                    </div>
                    {m.measures && m.measures.length > 1 && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        +{m.measures.length - 1}개
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {m.regulations && m.regulations.length > 0 ? (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                        {m.regulations.length}
                      </span>
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-3 py-3">{getSourceBadge(m.source)}</td>
                  <td className="px-3 py-3 text-sm text-gray-500 text-center">{m.usageCount}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* 페이지네이션 */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
          총 {total.toLocaleString()}건 중 {(page - 1) * 20 + 1}-{Math.min(page * 20, total)}건
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            이전
          </button>
          <span className="px-3 py-1">
            {page} / {totalPages || 1}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || totalPages === 0}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            다음
          </button>
        </div>
      </div>

      {/* 상세 모달 */}
      {detailModal && (
        // 종전 패널 스크롤(max-h-[90vh])은 base 본문 스크롤이 맡는다 — 헤더·버튼 줄이 고정된다.
        <Modal
          open
          onClose={() => setDetailModal(null)}
          title="지식 상세"
          size="xl"
          footer={
            <div className="flex w-full justify-between">
              <button
                onClick={() => {
                  const target = detailModal;
                  setDetailModal(null);
                  handleDelete(target.id);
                }}
                className="px-4 py-2 text-red-600 border border-red-300 rounded-control hover:bg-red-50"
              >
                삭제
              </button>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    const target = detailModal;
                    setDetailModal(null);
                    openEditModal(target);
                  }}
                >
                  수정
                </Button>
                <Button variant="secondary" onClick={() => setDetailModal(null)}>
                  닫기
                </Button>
              </div>
            </div>
          }
        >
            <div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">분류 체계</label>
                  <div className="mt-1">
                    {detailModal.industryType && <span className="text-brand-600">{detailModal.industryType}</span>}
                    {detailModal.industryType && detailModal.category && ' > '}
                    {detailModal.category}
                    {detailModal.subCategory && ` > ${detailModal.subCategory}`}
                    {detailModal.detailCategory && ` > ${detailModal.detailCategory}`}
                  </div>
                </div>

                {detailModal.hazardFactor && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">위험요인</label>
                    <div className="mt-1">{detailModal.hazardFactor}</div>
                  </div>
                )}

                {detailModal.riskSituation && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">위험상황</label>
                    <div className="mt-1">{detailModal.riskSituation}</div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    개선 대책 ({detailModal.measures.length}개)
                  </label>
                  {detailModal.measures.length > 0 ? (
                    <ul className="mt-1 space-y-2">
                      {detailModal.measures.map((km) => (
                        <li key={km.id} className="p-3 bg-gray-50 rounded-card">
                          <div className="text-sm">{km.content}</div>
                          <div className="flex gap-4 mt-1 text-xs text-gray-400">
                            {km.confidence !== null && (
                              <span>신뢰도: {Math.round(km.confidence * 100)}%</span>
                            )}
                            <span>사용: {km.usageCount}회</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="mt-1 p-3 bg-gray-50 rounded-card whitespace-pre-wrap text-sm text-gray-400">
                      -
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">출처</label>
                    <div className="mt-1">{getSourceBadge(detailModal.source)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">신뢰도</label>
                    <div className="mt-1">
                      {detailModal.confidence !== null
                        ? `${Math.round(detailModal.confidence * 100)}%`
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">사용 횟수</label>
                    <div className="mt-1">{detailModal.usageCount}</div>
                  </div>
                </div>

                {detailModal.regulations && detailModal.regulations.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">법적 근거 (매칭)</label>
                    <div className="mt-1 space-y-2">
                      {detailModal.regulations.map((reg) => (
                        <div key={reg.id} className="p-3 bg-gray-50 rounded-card text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{reg.type}</span>
                            {reg.relevance != null && (
                              <span className="text-xs bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded">
                                {Math.round(reg.relevance * 100)}%
                              </span>
                            )}
                          </div>
                          {reg.article && <div className="text-gray-500">조항: {[reg.code, reg.title].filter(Boolean).join(' ') || reg.article}</div>}
                          {reg.summary && <div className="text-gray-600 mt-1">{reg.summary}</div>}
                          {reg.url && (
                            <a
                              href={reg.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-brand-600 hover:underline text-xs mt-1 inline-block"
                            >
                              법령 링크
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                  <div>생성일: {new Date(detailModal.createdAt).toLocaleString('ko-KR')}</div>
                  <div>수정일: {new Date(detailModal.updatedAt).toLocaleString('ko-KR')}</div>
                </div>
              </div>
            </div>
        </Modal>
      )}

      {/* 생성/수정 모달 */}
      {(createModal || editModal) && (
        <Modal
          open
          onClose={closeEditFormModal}
          title={createModal ? '수동 등록' : '지식 수정'}
          size="lg"
          isDirty={isEditFormDirty}
          footer={
            <>
              <ModalCancelButton onCancel={closeEditFormModal} />
              <Button
                variant="primary"
                onClick={createModal ? handleCreate : handleUpdate}
              >
                {createModal ? '등록' : '저장'}
              </Button>
            </>
          }
        >
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">카테고리 *</label>
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                    className="w-full px-3 py-2 border rounded-control"
                    placeholder="예: 추락 위험"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">서브카테고리</label>
                  <input
                    type="text"
                    value={editForm.subCategory}
                    onChange={(e) => setEditForm({ ...editForm, subCategory: e.target.value })}
                    className="w-full px-3 py-2 border rounded-control"
                    placeholder="예: 사다리 작업"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">위험요인</label>
                  <input
                    type="text"
                    value={editForm.hazardFactor}
                    onChange={(e) => setEditForm({ ...editForm, hazardFactor: e.target.value })}
                    className="w-full px-3 py-2 border rounded-control"
                    placeholder="예: 안전대 미착용"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">위험상황</label>
                  <input
                    type="text"
                    value={editForm.riskSituation}
                    onChange={(e) => setEditForm({ ...editForm, riskSituation: e.target.value })}
                    className="w-full px-3 py-2 border rounded-control"
                    placeholder="예: 고소작업 시 추락"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    개선 대책 * ({editForm.measureInputs.filter(m => m.trim()).length}개)
                  </label>
                  <div className="space-y-2">
                    {editForm.measureInputs.map((measure, idx) => (
                      <div key={idx} className="flex gap-2">
                        <textarea
                          value={measure}
                          onChange={(e) => {
                            const newMeasures = [...editForm.measureInputs];
                            newMeasures[idx] = e.target.value;
                            setEditForm({ ...editForm, measureInputs: newMeasures });
                          }}
                          className="flex-1 px-3 py-2 border rounded-control"
                          rows={2}
                          placeholder={`대책 ${idx + 1}`}
                        />
                        {editForm.measureInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const newMeasures = editForm.measureInputs.filter((_, i) => i !== idx);
                              setEditForm({ ...editForm, measureInputs: newMeasures });
                            }}
                            className="px-2 text-red-500 hover:text-red-700 self-start mt-2"
                            title="삭제"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() =>
                        setEditForm({ ...editForm, measureInputs: [...editForm.measureInputs, ''] })
                      }
                      className="text-sm text-brand-600 hover:text-brand-800"
                    >
                      + 대책 추가
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">출처</label>
                  <select
                    value={editForm.source}
                    onChange={(e) =>
                      setEditForm({ ...editForm, source: e.target.value as HazardKnowledgeSource })
                    }
                    className="w-full px-3 py-2 border rounded-control"
                  >
                    <option value="USER">수동 등록</option>
                    <option value="EXTERNAL">외부 데이터</option>
                    <option value="SIF">SIF 데이터</option>
                    <option value="SYSTEM">시스템</option>
                  </select>
                </div>
              </div>
            </div>
        </Modal>
      )}

      {/* Import 모달 */}
      {importModal && (
        // 헤더는 base 것을 쓰지 않는다 — 매핑 단계의 분석 요약(`details`)이 제목 줄에 붙어 있고, ✕가 단계에 따라
        // 사라진다. 그래서 title·✕ 없이(= base 헤더 없음) 종전 헤더를 본문 첫 줄로 그대로 둔다.
        // 분석·가져오기 중에는 종전처럼 바깥 클릭이 안 닫고(종전 onClose=undefined) Esc도 같이 막는다.
        // 매핑 단계는 종전처럼 높이를 90vh로 고정하고, 본문 안쪽 열이 `h-full`로 그 높이를 받아
        // 매핑 표가 자기 영역에서 스크롤한다(본문 flex-1이 확정 높이라 h-full이 풀린다).
        <Modal
          open
          onClose={closeImportModal}
          ariaLabel="데이터 Import"
          size="xl"
          showCloseButton={false}
          closeOnOverlay={!(importStep === 'importing' || importStep === 'analyze')}
          closeOnEsc={!(importStep === 'importing' || importStep === 'analyze')}
          closeLocked={importStep === 'importing' || importStep === 'analyze'}
          isDirty={isImportDirty}
          panelClassName={
            importStep === 'mapping'
              ? '!h-[90vh] !w-[56rem] !max-w-[calc(100vw-2rem)]'
              : '!max-w-4xl'
          }
        >
            <div className="flex flex-col h-full min-h-0">
              <div className="flex items-center justify-between mb-4 shrink-0">
                <div className="flex items-center gap-3 min-w-0">
                  <h2 className="text-lg font-bold shrink-0">데이터 Import</h2>
                  {importStep === 'mapping' && analysisResult?.comprehensiveAnalysis && (
                    <details className="min-w-0">
                      <summary className="cursor-pointer flex items-center gap-2 px-2 py-1 rounded-control bg-purple-50 border border-purple-200 text-sm">
                        <span className="text-purple-800 font-medium truncate">
                          {analysisResult.comprehensiveAnalysis.documentType}
                        </span>
                        <span className="text-xs text-gray-500 shrink-0">
                          ({Math.round(analysisResult.comprehensiveAnalysis.overallConfidence * 100)}%)
                        </span>
                      </summary>
                      {analysisResult.comprehensiveAnalysis.reasoning && (
                        <p className="mt-1 px-2 pb-1 text-xs text-gray-600">
                          {analysisResult.comprehensiveAnalysis.reasoning}
                        </p>
                      )}
                    </details>
                  )}
                </div>
                {importStep !== 'importing' && importStep !== 'analyze' && (
                  <ImportHeaderCloseButton onClose={closeImportModal} />
                )}
              </div>

              {/* Step 1: Import 모드 선택 */}
              {importStep === 'select' && (
                <>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* SIF Import */}
                    <div
                      className={`border-2 rounded-card p-6 cursor-pointer transition-all ${
                        importMode === 'sif'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-orange-300'
                      }`}
                      onClick={() => setImportMode('sif')}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-card flex items-center justify-center">
                          <span className="text-2xl">📊</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">SIF 데이터 Import</h3>
                          <p className="text-sm text-gray-500">중대재해 사례 데이터</p>
                        </div>
                      </div>
                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                        <li>- 제조업/건설업 시트 자동 인식</li>
                        <li>- 사고유형, 기인물, 가해물 파싱</li>
                        <li>- 재발방지대책 자동 추출</li>
                      </ul>
                    </div>

                    {/* 동적 Import */}
                    <div
                      className={`border-2 rounded-card p-6 cursor-pointer transition-all ${
                        importMode === 'dynamic'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                      onClick={() => setImportMode('dynamic')}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-card flex items-center justify-center">
                          <span className="text-2xl">📁</span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">동적 Import</h3>
                          <p className="text-sm text-gray-500">커스텀 엑셀 파일</p>
                        </div>
                      </div>
                      <ul className="text-sm text-gray-600 space-y-1 ml-4">
                        <li>- 칼럼 자동 매핑 제안</li>
                        <li>- 수동 매핑 조정 가능</li>
                        <li>- 다양한 포맷 지원</li>
                      </ul>
                    </div>
                  </div>

                  {/* 파일 선택 */}
                  {importMode && (
                    <div className="pt-6 border-t">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        엑셀 파일 선택{importMode === 'dynamic' ? ' (여러 파일 선택 가능)' : ''}
                      </label>
                      <div className="flex gap-4 items-center">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls,.xlsm"
                          multiple={importMode === 'dynamic'}
                          onChange={handleFileSelect}
                          className="flex-1 px-3 py-2 border rounded-control"
                        />
                        <div className="relative group">
                          <button
                            onClick={() => {
                              if (importMode === 'sif') {
                                handleSifImport();
                              } else {
                                analyzeFiles();
                              }
                            }}
                            disabled={selectedFiles.length === 0 || importLoading || (importMode === 'dynamic' && selectedFiles.length > 100)}
                            className={`px-6 py-2 rounded-control text-white font-medium transition-colors ${
                              importMode === 'sif'
                                ? 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300'
                                : 'bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300'
                            }`}
                          >
                            {importLoading
                              ? '처리 중...'
                              : importMode === 'sif'
                              ? 'SIF Import 시작'
                              : `파일 분석${selectedFiles.length > 1 ? ` (${selectedFiles.length}개)` : ''}`}
                          </button>
                          {importMode === 'dynamic' && selectedFiles.length > 100 && (
                            <div className="absolute top-full right-0 mt-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-card-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                              한 번에 최대 100개 파일까지 분석할 수 있습니다
                              <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900" />
                            </div>
                          )}
                        </div>
                      </div>
                      {selectedFiles.length > 0 && (
                        <div className="mt-2 text-sm text-gray-500">
                          {selectedFiles.length === 1 ? (
                            <p>선택된 파일: {selectedFiles[0].name} ({(selectedFiles[0].size / 1024).toFixed(1)} KB)</p>
                          ) : (
                            <div className="relative group/files inline-block">
                              <p className="cursor-default">
                                선택된 파일: <span className="text-gray-700 font-medium">{selectedFiles[0].name}</span>
                                {' '}외 {selectedFiles.length - 1}건
                                <span className="text-gray-400 ml-1">
                                  ({selectedFiles.length}개, 총 {(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024).toFixed(1)} KB)
                                </span>
                              </p>
                              <div className="absolute left-0 bottom-full pb-2 invisible group-hover/files:visible z-50">
                                <div className="p-3 bg-gray-900 text-white text-xs rounded-card-sm shadow-xl max-h-48 overflow-y-auto w-80">
                                  <div className="font-medium mb-1.5 text-gray-300">선택된 파일 ({selectedFiles.length}개)</div>
                                  {selectedFiles.map((f, i) => (
                                    <div key={i} className="py-0.5 truncate">{f.name}</div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Step 2: 분석 중 */}
              {importStep === 'analyze' && importLoading && (
                <div className="py-12 text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mb-4" />
                  <h3 className="text-lg font-semibold">
                    AI 분석 중...{analyzeProgress && analyzeProgress.total > 1 && ` (${analyzeProgress.completed}/${analyzeProgress.total} 파일 완료)`}
                  </h3>
                  {analyzeProgress && analyzeProgress.activeFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {analyzeProgress.activeFiles.map(name => (
                        <p key={name} className="text-gray-600 font-medium text-sm flex items-center justify-center gap-1.5">
                          <span className="inline-block w-3 h-3 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
                          {name}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-gray-500 mt-2">
                    {analyzeProgress && analyzeProgress.total > 1
                      ? `${Math.min(5, analyzeProgress.total)}개 파일을 동시에 분석합니다.`
                      : 'AI가 시트 구조와 칼럼을 분석하고 최적 매핑을 추천합니다.'}
                  </p>
                  {analyzeProgress && analyzeProgress.total > 1 && (
                    <div className="max-w-xs mx-auto mt-4">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(analyzeProgress.completed / analyzeProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: 칼럼 매핑 */}
              {importStep === 'mapping' && analysisResult && (
                <div className="flex flex-col flex-1 min-h-0">
                  {/* 파일 선택 - 2개 이상일 때만 표시 */}
                  {selectedFiles.length > 1 && (
                    <div className="shrink-0 mb-3 flex items-center gap-2 border-b pb-2">
                      <button
                        onClick={() => handleFileChange(currentFileIndex - 1)}
                        disabled={currentFileIndex === 0}
                        className="p-1.5 rounded-control border text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="이전 파일"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <select
                        value={currentFileIndex}
                        onChange={(e) => handleFileChange(Number(e.target.value))}
                        className="flex-1 border rounded-control px-3 py-1.5 text-sm min-w-0"
                      >
                        {selectedFiles.map((file, idx) => {
                          const fileAnalysis = fileAnalysisMap.get(file.name);
                          let readySheetCount = 0;
                          if (fileAnalysis) {
                            const curConfigKey = selectedFile ? getSheetConfigKey(selectedFile.name, selectedSheet) : '';
                            const curConfig: PerSheetConfig = {
                              columnMapping: { ...columnMapping },
                              classificationColumns: [...classificationColumns],
                              excludedRows: new Set(excludedRows),
                              detectedHeaderRows: [...detectedHeaderRows],
                              detectedDataStartRow,
                              addToClassification,
                            };
                            for (const sheet of fileAnalysis.sheets) {
                              const key = getSheetConfigKey(file.name, sheet.name);
                              const config = key === curConfigKey ? curConfig : sheetConfigs.get(key);
                              if (config && checkSheetCanImport(config) && getMissingColumnFields(config).length === 0 && getValidRowCount(sheet.name, config, fileAnalysis) > 0) readySheetCount++;
                            }
                          }
                          return (
                            <option key={file.name} value={idx} disabled={!fileAnalysis}>
                              {file.name}{readySheetCount > 0 ? ` (${readySheetCount}시트 준비)` : ''}{!fileAnalysis ? ' (분석 실패)' : ''}
                            </option>
                          );
                        })}
                      </select>
                      <button
                        onClick={() => handleFileChange(currentFileIndex + 1)}
                        disabled={currentFileIndex >= selectedFiles.length - 1}
                        className="p-1.5 rounded-control border text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:hover:bg-transparent"
                        title="다음 파일"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                      <span className="shrink-0 text-xs text-gray-400">{currentFileIndex + 1}/{selectedFiles.length}</span>
                    </div>
                  )}

                  {/* 시트 선택 - 횡 탭 */}
                  <div className="shrink-0 mb-3">
                    <div className="flex gap-1.5 overflow-x-auto pb-1">
                      {(() => {
                        // 시트별 등록 가능 여부 사전 계산 (정렬용)
                        const curConfigKey = selectedFile ? getSheetConfigKey(selectedFile.name, selectedSheet) : '';
                        const curConfig: PerSheetConfig = {
                          columnMapping: { ...columnMapping },
                          classificationColumns: [...classificationColumns],
                          excludedRows: new Set(excludedRows),
                          detectedHeaderRows: [...detectedHeaderRows],
                          detectedDataStartRow,
                          addToClassification,
                        };

                        const sheetInfos = analysisResult.sheets.map((sheet) => {
                          const key = selectedFile ? getSheetConfigKey(selectedFile.name, sheet.name) : '';
                          const config = key === curConfigKey ? curConfig : sheetConfigs.get(key);
                          const missingRequired = config ? getMissingRequiredFields(config) : KNOWLEDGE_FIELDS.filter(f => f.required).map(f => f.label);
                          const missingColumns = config ? getMissingColumnFields(config) : [];
                          const canImport = config ? missingRequired.length === 0 : false;
                          const validCount = canImport ? getValidRowCount(sheet.name, config!, analysisResult) : 0;
                          const isFullyMapped = canImport && missingColumns.length === 0 && validCount > 0;
                          // 후보: config 있고 (필수 1-2개 누락 OR 필수 충족이지만 칼럼 매핑 불완전)
                          const allMissing = [...new Set([...missingRequired, ...missingColumns])];
                          const isCandidate = !isFullyMapped && !!config && allMissing.length > 0 && allMissing.length <= 2;
                          return { sheet, config, isReady: isFullyMapped, isCandidate, missing: allMissing, validCount };
                        });

                        // 정렬: ready → candidate → 나머지
                        return [...sheetInfos]
                          .sort((a, b) => {
                            const aOrder = a.isReady ? 2 : a.isCandidate ? 1 : 0;
                            const bOrder = b.isReady ? 2 : b.isCandidate ? 1 : 0;
                            return bOrder - aOrder;
                          })
                          .map(({ sheet, config, isReady, isCandidate, missing, validCount }) => {
                            const isActive = selectedSheet === sheet.name;

                            // 전체 유효행 (excludedRows 무시) — 선택/전체 표시용
                            let totalValid = 0;
                            if (isReady && config) {
                              const compSheet = analysisResult.comprehensiveAnalysis?.importableSheets.find(s => s.sheetName === sheet.name);
                              const requiredFieldKeys = KNOWLEDGE_FIELDS.filter(f => !f.canInfer && f.required).map(f => f.key);
                              const isCurrentSheet = selectedSheet === sheet.name;
                              let mappedIndices: number[] = [];
                              if (isCurrentSheet) {
                                mappedIndices = requiredFieldKeys
                                  .map((k) => { const v = columnMapping[k]; return typeof v === 'number' && v >= 0 ? v : -1; })
                                  .filter((idx) => idx >= 0);
                              } else {
                                mappedIndices = requiredFieldKeys
                                  .map((k) => { const v = config.columnMapping[k]; return typeof v === 'number' && v >= 0 ? v : -1; })
                                  .filter((idx) => idx >= 0);
                              }
                              if (mappedIndices.length >= requiredFieldKeys.length) {
                                const dataStartRow = config.detectedDataStartRow ?? compSheet?.dataStartRow ?? (sheet.rawPreview?.[0]?.row ?? 0) + 1;
                                totalValid = (sheet.rawPreview ?? [])
                                  .filter((r) => r.row > dataStartRow - 1)
                                  .filter((r) => {
                                    const rowData = r.data as unknown[];
                                    return mappedIndices.every((idx) => { const v = rowData[idx]; return v !== undefined && v !== null && v !== ''; });
                                  }).length;
                              }
                            }

                            return (
                              <button
                                key={sheet.name}
                                onClick={() => handleSheetChange(sheet.name)}
                                title={isCandidate ? `누락: ${missing.join(', ')}` : undefined}
                                className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-control text-xs font-medium border transition-colors ${
                                  isActive
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {sheet.name}
                                {isReady && totalValid > 0 && (
                                  <span className={`${isActive ? 'text-indigo-200' : 'text-gray-400'}`}>
                                    ({validCount}/{totalValid})
                                  </span>
                                )}
                                {isReady && <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />}
                                {isCandidate && <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" title={`누락: ${missing.join(', ')}`} />}
                              </button>
                            );
                          });
                      })()}
                    </div>
                  </div>

                  {/* 여러 시트/파일 Import 가이드 - 실제 매핑 유효한 시트 수 기준 */}
                  {(() => {
                    const currentConfigKey = selectedFile ? getSheetConfigKey(selectedFile.name, selectedSheet) : '';
                    const currentConfig: PerSheetConfig = {
                      columnMapping: { ...columnMapping },
                      classificationColumns: [...classificationColumns],
                      excludedRows: new Set(excludedRows),
                      detectedHeaderRows: [...detectedHeaderRows],
                      detectedDataStartRow,
                      addToClassification,
                    };
                    let readyCount = 0;
                    const readyFileSet = new Set<string>();
                    const checked = new Set<string>();
                    for (const file of selectedFiles) {
                      const analysis = fileAnalysisMap.get(file.name);
                      if (!analysis) continue;
                      for (const sheet of analysis.sheets) {
                        const key = getSheetConfigKey(file.name, sheet.name);
                        const config = key === currentConfigKey ? currentConfig : sheetConfigs.get(key);
                        if (!config || !checkSheetCanImport(config) || getMissingColumnFields(config).length > 0) continue;
                        const validCount = getValidRowCount(sheet.name, config, analysis);
                        if (validCount <= 0) continue;
                        readyCount++;
                        checked.add(key);
                        readyFileSet.add(file.name);
                      }
                    }
                    if (!checked.has(currentConfigKey) && checkSheetCanImport(currentConfig) && getMissingColumnFields(currentConfig).length === 0) {
                      const validCount = getValidRowCount(selectedSheet, currentConfig);
                      if (validCount > 0) {
                        readyCount++;
                        if (selectedFile) readyFileSet.add(selectedFile.name);
                      }
                    }
                    if (readyCount <= 1) return null;
                    const readyFileCount = readyFileSet.size;
                    return (
                      <div className="shrink-0 mb-3 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-card text-xs text-indigo-700">
                        {selectedFiles.length > 1
                          ? `${readyFileCount}개 파일에서 매핑이 완료된 시트가 총 ${readyCount}개 있습니다.`
                          : `매핑이 완료된 시트가 ${readyCount}개 있습니다.`}
                        {' '}각 시트별로 행 선택을 확인한 후, 하단의 등록 버튼을 누르면 모든 {selectedFiles.length > 1 ? '파일의 ' : ''}시트가 한번에 등록됩니다.
                      </div>
                    );
                  })()}

                  {/* 스크롤 영역 */}
                  <div className="flex-1 min-h-0 overflow-y-auto">
                  {/* 매핑 설정 */}
                  {currentSheetInfo && (
                      <div className="mb-6">
                        {/* 필드 매핑 */}
                        <div className="min-w-0 overflow-hidden">
                          <div className="flex items-center gap-2 mb-4">
                            <h3 className="font-medium">필드 매핑</h3>
                            {currentCompSheet && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                                AI 분석 완료
                              </span>
                            )}
                          </div>

                          {/* 분류 체계 + 칼럼 매핑 나란히 배치 */}
                          <div className="flex gap-6 mb-4">
                          {/* 분류 체계 그룹 - Cascading Dropdown */}
                          <div className="w-1/2 min-w-0">
                            <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                              <span className="font-medium">분류 체계</span>
                              {/* 공종-작업 분류에 추가 체크박스 */}
                              {(() => {
                                const hasNonDbSource = ['industryType', 'category', 'subCategory', 'detailCategory'].some(key => {
                                  const val = columnMapping[key];
                                  return typeof val === 'string' && (val.startsWith('inferred:') || val.startsWith('custom:'));
                                });
                                if (!hasNonDbSource) return null;
                                return (
                                  <label className="flex items-center gap-1 cursor-pointer ml-1">
                                    <input
                                      type="checkbox"
                                      checked={addToClassification}
                                      onChange={(e) => setAddToClassification(e.target.checked)}
                                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                    />
                                    <span className="text-xs text-gray-600">분류에 추가</span>
                                    <span className="text-xs text-gray-400">(체크 시 마스터 데이터에 등록)</span>
                                  </label>
                                );
                              })()}
                            </div>
                            <div className="space-y-2 pl-2 border-l-2 border-brand-200">
                              {/* 산업유형 */}
                              {(() => {
                                const field = KNOWLEDGE_FIELDS.find(f => f.key === 'industryType')!;
                                const inferredData = currentCompSheet?.inferredIndustryType;
                                const currentValue = columnMapping.industryType;
                                const isInferred = typeof currentValue === 'string' && currentValue.startsWith('inferred:');
                                const isCustom = typeof currentValue === 'string' && currentValue.startsWith('custom:');
                                // DB에서 찾은 값(접두사 없음), AI 유추값(inferred:), 사용자 입력(custom:) 모두 처리
                                const displayValue = isInferred
                                  ? currentValue.slice(9)
                                  : isCustom
                                  ? currentValue.slice(7)
                                  : (typeof currentValue === 'string' ? currentValue : '');
                                const hasValue = displayValue.trim() !== '';
                                const isFromDb = hasValue && !isInferred && !isCustom; // DB에서 찾은 값

                                return (
                                  <div className="flex items-center gap-2">
                                    <label className={`shrink-0 w-24 text-sm ${!hasValue && field.required ? 'text-red-600' : 'text-gray-700'}`}>
                                      {field.label}
                                      {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="flex-1 flex items-center gap-1">
                                      <select
                                        value={displayValue}
                                        onChange={async (e) => {
                                          const val = e.target.value;
                                          const hasChildren = columnMapping.category != null || columnMapping.subCategory != null;
                                          if (
                                            hasChildren &&
                                            !(await confirm({
                                              title: '산업유형 변경',
                                              body: '산업유형을 변경하면 대분류와 중분류가 초기화됩니다. 계속하시겠습니까?',
                                              confirmLabel: '변경',
                                              ...SUB_CONFIRM_LAYER,
                                            }))
                                          ) {
                                            e.target.value = displayValue; // 원복
                                            return;
                                          }
                                          if (val === '__custom__') {
                                            handleMappingChange('industryType', 'custom:');
                                          } else if (val) {
                                            handleMappingChange('industryType', val);
                                          } else {
                                            handleMappingChange('industryType', null);
                                          }
                                          if (hasChildren) {
                                            handleMappingChange('category', null);
                                            handleMappingChange('subCategory', null);
                                          }
                                        }}
                                        className={`flex-1 min-w-0 px-3 py-1.5 text-sm border rounded ${
                                          isFromDb ? 'bg-green-50 border-green-300 text-green-700' :
                                          isInferred ? 'bg-blue-50 border-blue-300 text-blue-700' :
                                          hasValue ? 'bg-white border-gray-300' :
                                          field.required ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300'
                                        }`}
                                      >
                                        <option value="">선택하세요</option>
                                        {/* AI 유추 값이 드롭다운에 없으면 별도 옵션으로 표시 */}
                                        {inferredData && !wcDropdownOptions?.industryTypes.includes(inferredData.value) && (
                                          <option value={inferredData.value}>
                                            {inferredData.value} (AI 유추)
                                          </option>
                                        )}
                                        {wcDropdownOptions?.industryTypes.map((it) => (
                                          <option key={it} value={it}>{it}</option>
                                        ))}
                                        <option value="__custom__">직접 입력...</option>
                                      </select>
                                      {isCustom && (
                                        <input
                                          type="text"
                                          value={displayValue}
                                          onChange={(e) => handleMappingChange('industryType', `custom:${e.target.value}`)}
                                          placeholder="직접 입력"
                                          className="flex-1 px-3 py-1.5 text-sm border rounded bg-yellow-50 border-yellow-300"
                                        />
                                      )}
                                      {isFromDb && (
                                        <span className="shrink-0 px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                                          DB
                                        </span>
                                      )}
                                      {inferredData && !isCustom && !isFromDb && (
                                        <button
                                          type="button"
                                          onClick={() => handleMappingChange('industryType', `inferred:${inferredData.value}`)}
                                          className={`shrink-0 px-2 py-1 text-xs rounded ${
                                            isInferred && displayValue === inferredData.value
                                              ? 'bg-blue-100 text-blue-700'
                                              : 'bg-gray-100 text-gray-600 hover:bg-blue-50'
                                          }`}
                                          title={inferredData.reasoning}
                                        >
                                          AI {Math.round(inferredData.confidence * 100)}%
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* 공종(category) */}
                              {(() => {
                                const field = KNOWLEDGE_FIELDS.find(f => f.key === 'category')!;
                                const inferredData = currentCompSheet?.inferredCategory;
                                const currentValue = columnMapping.category;
                                const isInferred = typeof currentValue === 'string' && currentValue.startsWith('inferred:');
                                const isCustom = typeof currentValue === 'string' && currentValue.startsWith('custom:');
                                const displayValue = isInferred
                                  ? currentValue.slice(9)
                                  : isCustom
                                  ? currentValue.slice(7)
                                  : (typeof currentValue === 'string' ? currentValue : '');
                                const hasValue = displayValue.trim() !== '';
                                const isFromDb = hasValue && !isInferred && !isCustom;

                                // 선택된 산업유형에 해당하는 공종만 필터링
                                const selectedIndustryType = (() => {
                                  const itVal = columnMapping.industryType;
                                  if (typeof itVal === 'string') {
                                    if (itVal.startsWith('inferred:')) return itVal.slice(9);
                                    if (itVal.startsWith('custom:')) return itVal.slice(7);
                                    return itVal; // 접두사 없는 일반 값
                                  }
                                  return '';
                                })();
                                const filteredCategories = wcDropdownOptions?.categories.filter(
                                  c => !selectedIndustryType || c.industryType === selectedIndustryType
                                ) || [];

                                return (
                                  <div className="flex items-center gap-2">
                                    <label className={`shrink-0 w-24 text-sm ${!hasValue && field.required ? 'text-red-600' : 'text-gray-700'}`}>
                                      {field.label}
                                      {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="flex-1 flex items-center gap-1">
                                      <select
                                        value={isCustom ? '__custom__' : displayValue}
                                        onChange={async (e) => {
                                          const val = e.target.value;
                                          const hasChild = columnMapping.subCategory != null;
                                          if (val === '__custom__') {
                                            handleMappingChange('category', 'custom:');
                                            handleMappingChange('subCategory', 'custom:');
                                          } else if (val) {
                                            if (
                                              hasChild &&
                                              !(await confirm({
                                                title: '대분류 변경',
                                                body: '대분류를 변경하면 중분류가 초기화됩니다. 계속하시겠습니까?',
                                                confirmLabel: '변경',
                                                ...SUB_CONFIRM_LAYER,
                                              }))
                                            ) {
                                              e.target.value = isCustom ? '__custom__' : displayValue;
                                              return;
                                            }
                                            handleMappingChange('category', val);
                                            if (hasChild) {
                                              handleMappingChange('subCategory', null);
                                            }
                                          } else {
                                            handleMappingChange('category', null);
                                            if (hasChild) {
                                              handleMappingChange('subCategory', null);
                                            }
                                          }
                                        }}
                                        className={`flex-1 min-w-0 px-3 py-1.5 text-sm border rounded ${
                                          isFromDb ? 'bg-green-50 border-green-300 text-green-700' :
                                          isInferred ? 'bg-blue-50 border-blue-300 text-blue-700' :
                                          isCustom ? 'bg-yellow-50 border-yellow-300' :
                                          hasValue ? 'bg-white border-gray-300' :
                                          field.required ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300'
                                        }`}
                                      >
                                        <option value="">선택하세요</option>
                                        {/* AI 유추 값이 드롭다운에 없으면 별도 옵션으로 표시 */}
                                        {inferredData && !filteredCategories.some(c => c.value === inferredData.value) && (
                                          <option value={inferredData.value}>
                                            {inferredData.value} (AI 유추)
                                          </option>
                                        )}
                                        {filteredCategories.map((c) => (
                                          <option key={`${c.industryType}-${c.value}`} value={c.value}>{c.value}</option>
                                        ))}
                                        <option value="__custom__">직접 입력...</option>
                                      </select>
                                      {isCustom && (
                                        <input
                                          type="text"
                                          value={displayValue}
                                          onChange={(e) => handleMappingChange('category', `custom:${e.target.value}`)}
                                          placeholder="직접 입력"
                                          className="flex-1 px-3 py-1.5 text-sm border rounded bg-yellow-50 border-yellow-300"
                                        />
                                      )}
                                      {isFromDb && (
                                        <span className="shrink-0 px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                                          DB
                                        </span>
                                      )}
                                      {inferredData && !isCustom && !isFromDb && (
                                        <button
                                          type="button"
                                          onClick={() => handleMappingChange('category', `inferred:${inferredData.value}`)}
                                          className={`shrink-0 px-2 py-1 text-xs rounded ${
                                            isInferred && displayValue === inferredData.value
                                              ? 'bg-blue-100 text-blue-700'
                                              : 'bg-gray-100 text-gray-600 hover:bg-blue-50'
                                          }`}
                                          title={inferredData.reasoning}
                                        >
                                          AI {Math.round(inferredData.confidence * 100)}%
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* 작업명(subCategory) */}
                              {(() => {
                                const field = KNOWLEDGE_FIELDS.find(f => f.key === 'subCategory')!;
                                const inferredData = currentCompSheet?.inferredSubCategory;
                                const currentValue = columnMapping.subCategory;
                                const isInferred = typeof currentValue === 'string' && currentValue.startsWith('inferred:');
                                const isCustom = typeof currentValue === 'string' && currentValue.startsWith('custom:');
                                const displayValue = isInferred
                                  ? currentValue.slice(9)
                                  : isCustom
                                  ? currentValue.slice(7)
                                  : (typeof currentValue === 'string' ? currentValue : '');
                                const hasValue = displayValue.trim() !== '';
                                const isFromDb = hasValue && !isInferred && !isCustom;

                                // 선택된 산업유형과 공종에 해당하는 작업명만 필터링
                                const selectedIndustryType = (() => {
                                  const itVal = columnMapping.industryType;
                                  if (typeof itVal === 'string') {
                                    if (itVal.startsWith('inferred:')) return itVal.slice(9);
                                    if (itVal.startsWith('custom:')) return itVal.slice(7);
                                    return itVal;
                                  }
                                  return '';
                                })();
                                const selectedCategory = (() => {
                                  const catVal = columnMapping.category;
                                  if (typeof catVal === 'string') {
                                    if (catVal.startsWith('inferred:')) return catVal.slice(9);
                                    if (catVal.startsWith('custom:')) return catVal.slice(7);
                                    return catVal;
                                  }
                                  return '';
                                })();
                                const filteredSubCategories = wcDropdownOptions?.subCategories.filter(
                                  s => (!selectedIndustryType || s.industryType === selectedIndustryType) &&
                                       (!selectedCategory || s.category === selectedCategory)
                                ) || [];

                                // 공종이 직접 입력 모드면 작업명도 직접 입력만 가능
                                const categoryIsCustom = typeof columnMapping.category === 'string' && columnMapping.category.startsWith('custom:');

                                return (
                                  <div className="flex items-center gap-2">
                                    <label className={`shrink-0 w-24 text-sm ${!hasValue && field.required ? 'text-red-600' : 'text-gray-700'}`}>
                                      {field.label}
                                      {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <div className="flex-1 flex items-center gap-1">
                                      {categoryIsCustom ? (
                                        // 공종이 직접 입력이면 작업명도 직접 입력만 가능
                                        <input
                                          type="text"
                                          value={displayValue}
                                          onChange={(e) => handleMappingChange('subCategory', `custom:${e.target.value}`)}
                                          placeholder="직접 입력 (공종이 직접 입력됨)"
                                          className="flex-1 px-3 py-1.5 text-sm border rounded bg-yellow-50 border-yellow-300"
                                        />
                                      ) : (
                                        <>
                                          <select
                                            value={isCustom ? '__custom__' : displayValue}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              if (val === '__custom__') {
                                                handleMappingChange('subCategory', 'custom:');
                                              } else if (val) {
                                                handleMappingChange('subCategory', val);
                                                // 작업명 선택 시 상위 공종 자동 선택
                                                const matchedSub = wcDropdownOptions?.subCategories.find(s => s.value === val);
                                                if (matchedSub && !selectedCategory) {
                                                  handleMappingChange('category', matchedSub.category);
                                                  if (!selectedIndustryType) {
                                                    handleMappingChange('industryType', matchedSub.industryType);
                                                  }
                                                }
                                              } else {
                                                handleMappingChange('subCategory', null);
                                              }
                                            }}
                                            className={`flex-1 min-w-0 px-3 py-1.5 text-sm border rounded ${
                                              isFromDb ? 'bg-green-50 border-green-300 text-green-700' :
                                              isInferred ? 'bg-blue-50 border-blue-300 text-blue-700' :
                                              isCustom ? 'bg-yellow-50 border-yellow-300' :
                                              hasValue ? 'bg-white border-gray-300' :
                                              field.required ? 'bg-red-50 border-red-300' : 'bg-white border-gray-300'
                                            }`}
                                          >
                                            <option value="">선택하세요</option>
                                            {/* AI 유추 값이 드롭다운에 없으면 별도 옵션으로 표시 */}
                                            {inferredData && !filteredSubCategories.some(s => s.value === inferredData.value) && (
                                              <option value={inferredData.value}>
                                                {inferredData.value} (AI 유추)
                                              </option>
                                            )}
                                            {filteredSubCategories.map((s) => (
                                              <option key={`${s.industryType}-${s.category}-${s.value}`} value={s.value}>{s.value}</option>
                                            ))}
                                            <option value="__custom__">직접 입력...</option>
                                          </select>
                                          {isCustom && (
                                            <input
                                              type="text"
                                              value={displayValue}
                                              onChange={(e) => handleMappingChange('subCategory', `custom:${e.target.value}`)}
                                              placeholder="직접 입력"
                                              className="flex-1 px-3 py-1.5 text-sm border rounded bg-yellow-50 border-yellow-300"
                                            />
                                          )}
                                          {isFromDb && (
                                            <span className="shrink-0 px-2 py-1 text-xs rounded bg-green-100 text-green-700">
                                              DB
                                            </span>
                                          )}
                                        </>
                                      )}
                                      {inferredData && !isCustom && !categoryIsCustom && !isFromDb && (
                                        <button
                                          type="button"
                                          onClick={() => handleMappingChange('subCategory', `inferred:${inferredData.value}`)}
                                          className={`shrink-0 px-2 py-1 text-xs rounded ${
                                            isInferred && displayValue === inferredData.value
                                              ? 'bg-blue-100 text-blue-700'
                                              : 'bg-gray-100 text-gray-600 hover:bg-blue-50'
                                          }`}
                                          title={inferredData.reasoning}
                                        >
                                          AI {Math.round(inferredData.confidence * 100)}%
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}

                            </div>

                          </div>

                          {/* 칼럼 매핑 그룹 */}
                          <div className="w-1/2 min-w-0">
                            <div className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                              <span className="font-medium">칼럼 매핑</span>
                              <span className="text-gray-400">(엑셀 칼럼 선택)</span>
                            </div>
                            <div className="space-y-2 pl-2 border-l-2 border-green-200">
                              {KNOWLEDGE_FIELDS.filter(f => !f.canInfer).map((field) => {
                                const compField = currentCompSheet?.fieldMapping?.[field.key];
                                const currentValue = columnMapping[field.key];
                                const selectValue = currentValue === null || currentValue === undefined
                                  ? ''
                                  : typeof currentValue === 'number'
                                  ? `idx:${currentValue}`
                                  : currentValue;
                                const hasValue = selectValue !== '';

                                return (
                                  <div key={field.key} className="flex items-center gap-2">
                                    <label className={`shrink-0 w-24 text-sm ${!hasValue && field.required ? 'text-red-600' : 'text-gray-700'}`}>
                                      {field.label}
                                      {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <select
                                      value={selectValue}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        if (!val) {
                                          handleMappingChange(field.key, null);
                                        } else if (val.startsWith('idx:')) {
                                          handleMappingChange(field.key, parseInt(val.slice(4), 10));
                                        } else {
                                          handleMappingChange(field.key, val);
                                        }
                                      }}
                                      className={`flex-1 min-w-0 px-3 py-1.5 text-sm border rounded ${
                                        hasValue
                                          ? 'bg-green-50 border-green-300'
                                          : field.required
                                          ? 'bg-red-50 border-red-300'
                                          : 'bg-white border-gray-300'
                                      }`}
                                    >
                                      <option value="">{field.required ? '칼럼 선택 필요' : '선택 안함'}</option>
                                      {columnOptions.map((opt) => (
                                        <option
                                          key={`idx:${opt.index}`}
                                          value={`idx:${opt.index}`}
                                        >
                                          [{opt.index}] {opt.name}
                                        </option>
                                      ))}
                                    </select>
                                    {compField?.confidence && (
                                      <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${
                                        compField.confidence >= 0.8 ? 'bg-green-100 text-green-700' :
                                        compField.confidence >= 0.5 ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        {Math.round(compField.confidence * 100)}%
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                  )}

                  {/* Import 데이터 미리보기 */}
                  {currentSheetInfo && (
                      <div className="mb-4">
                        <h3 className="font-medium mb-2">
                          Import 데이터
                          {currentCompSheet && detectedDataStartRow !== undefined && (
                            <span className="ml-2 text-xs font-normal text-gray-500">
                              ({detectedDataStartRow + 1}행부터)
                            </span>
                          )}
                        </h3>
                        {(() => {
                          // 표시할 필드 (매핑 칼럼 데이터에 집중 - 공종/작업명은 상단에서 지정)
                          const displayFields = KNOWLEDGE_FIELDS.filter(f =>
                            ['detailCategory', 'hazardFactor', 'measure'].includes(f.key)
                          );

                          // 각 필드에 대해 매핑된 칼럼 정보 가져오기
                          const getColumnInfo = (fieldKey: string) => {
                            const mappingValue = columnMapping[fieldKey];
                            if (mappingValue === null || mappingValue === undefined) {
                              return { mapped: false, index: -1, name: '없음' };
                            }
                            if (typeof mappingValue === 'number') {
                              const opt = columnOptions.find((o) => o.index === mappingValue);
                              return { mapped: true, index: mappingValue, name: opt?.name || `칼럼${mappingValue}` };
                            }
                            return { mapped: true, index: -1, name: mappingValue };
                          };

                          // 문자열 값 가져오기 (AI 유추, 사용자 입력, DB 값 모두 처리)
                          const getStringValue = (fieldKey: string): { value: string | null; type: 'inferred' | 'custom' | 'db' | null } => {
                            const mappingValue = columnMapping[fieldKey];
                            if (typeof mappingValue === 'string') {
                              if (mappingValue.startsWith('inferred:')) {
                                return { value: mappingValue.slice(9), type: 'inferred' };
                              }
                              if (mappingValue.startsWith('custom:')) {
                                return { value: mappingValue.slice(7), type: 'custom' };
                              }
                              // 접두사 없는 문자열 = DB에서 찾은 값
                              if (mappingValue.trim()) {
                                return { value: mappingValue, type: 'db' };
                              }
                            }
                            return { value: null, type: null };
                          };

                          // 필수 필드들의 칼럼 인덱스 (소분류/단위작업, 유해위험요인, 감소대책)
                          const requiredFieldKeys = ['detailCategory', 'hazardFactor', 'measure'] as const;
                          const requiredColumnIndices = requiredFieldKeys
                            .map((key) => getColumnInfo(key))
                            .filter((info) => info.mapped && info.index >= 0)
                            .map((info) => info.index);

                          // 필수 필드에 값이 있는 행인지 확인
                          const hasRequiredValues = (rowData: unknown[]): boolean => {
                            // 매핑된 필수 칼럼이 없으면 true (필터링 안 함)
                            if (requiredColumnIndices.length === 0) return true;
                            // 모든 필수 칼럼에 값이 있어야 함
                            return requiredColumnIndices.every((idx) => {
                              const val = rowData[idx];
                              return val !== undefined && val !== null && val !== '';
                            });
                          };

                          // 유효한 데이터 행만 필터링
                          // compSheet가 없어도 rawPreview와 columnMapping이 있으면 동적 필터링 적용
                          let validDataRows: Array<{ row: number; data: unknown[] }> | null = null;

                          // 필수 필드 3개(단위작업, 유해위험요인, 감소대책) 모두 매핑되어야 데이터 표시
                          if (requiredColumnIndices.length >= requiredFieldKeys.length && currentSheetInfo.rawPreview && currentSheetInfo.rawPreview.length > 0) {
                            // dataStartRow: 헤더 행 다음 행부터 데이터
                            const effectiveDataStartRow = detectedDataStartRow !== undefined
                              ? detectedDataStartRow
                              : detectedHeaderRowNumber + 1;

                            validDataRows = currentSheetInfo.rawPreview
                              .filter((r) => r.row > effectiveDataStartRow - 1) // 헤더 행 제외
                              .filter((r) => hasRequiredValues(r.data as unknown[]));
                          } else {
                            // 필수 필드가 모두 매핑되지 않으면 빈 배열
                            validDataRows = [];
                          }

                          // 전체 데이터 개수 계산
                          const totalDataCount = validDataRows
                            ? validDataRows.length
                            : currentSheetInfo.sampleData.length;

                          const selectedCount = totalDataCount - excludedRows.size;

                          return (
                            <>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">
                                총 {totalDataCount}건 중 <span className="font-medium text-purple-600">{selectedCount}건</span> 선택
                              </span>
                            </div>
                            <div className="border rounded-card">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                  <tr>
                                    <th className="px-2 py-2 text-center w-10">
                                      <input
                                        type="checkbox"
                                        checked={excludedRows.size === 0}
                                        ref={(el) => {
                                          if (el) el.indeterminate = excludedRows.size > 0 && excludedRows.size < totalDataCount;
                                        }}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setExcludedRows(new Set());
                                          } else {
                                            const allRowNums = new Set<number>();
                                            if (validDataRows) {
                                              validDataRows.forEach((r) => allRowNums.add(r.row));
                                            } else {
                                              currentSheetInfo.sampleData.forEach((_, idx) => allRowNums.add(idx));
                                            }
                                            setExcludedRows(allRowNums);
                                          }
                                        }}
                                        className="rounded"
                                      />
                                    </th>
                                    {displayFields.map((field) => {
                                      const colInfo = getColumnInfo(field.key);
                                      const strVal = getStringValue(field.key);
                                      return (
                                        <th
                                          key={field.key}
                                          className="px-4 py-2 text-left font-medium text-gray-600"
                                        >
                                          <div>{field.label}</div>
                                          <div className={`text-xs font-normal ${
                                            strVal.type === 'inferred' ? 'text-blue-600' :
                                            strVal.type === 'db' ? 'text-green-600' :
                                            strVal.type === 'custom' ? 'text-yellow-600' :
                                            colInfo.mapped ? 'text-green-600' : 'text-gray-400'
                                          }`}>
                                            {strVal.type === 'inferred'
                                              ? `(AI: ${strVal.value})`
                                              : strVal.type === 'db'
                                              ? `(DB: ${strVal.value})`
                                              : strVal.type === 'custom'
                                              ? `(입력: ${strVal.value})`
                                              : colInfo.mapped
                                              ? `([${colInfo.index}] ${colInfo.name})`
                                              : '(미설정)'}
                                          </div>
                                        </th>
                                      );
                                    })}
                                  </tr>
                                </thead>
                                <tbody>
                                  {/* 종합 분석: rawPreview에서 dataStartRow 이후 + 필수 필드 값이 있는 행만 표시 */}
                                  {validDataRows ? (
                                    validDataRows
                                      .map((rawRow) => (
                                        <tr
                                          key={rawRow.row}
                                          className={`border-t hover:bg-gray-50 cursor-pointer ${excludedRows.has(rawRow.row) ? 'opacity-40' : ''}`}
                                          onClick={() => {
                                            setExcludedRows((prev) => {
                                              const next = new Set(prev);
                                              if (next.has(rawRow.row)) {
                                                next.delete(rawRow.row);
                                              } else {
                                                next.add(rawRow.row);
                                              }
                                              return next;
                                            });
                                          }}
                                        >
                                          <td className="px-2 py-2 text-center w-10">
                                            <input
                                              type="checkbox"
                                              checked={!excludedRows.has(rawRow.row)}
                                              readOnly
                                              className="rounded pointer-events-none"
                                            />
                                          </td>
                                          {displayFields.map((field) => {
                                            const colInfo = getColumnInfo(field.key);
                                            const strVal = getStringValue(field.key);
                                            // 칼럼 매핑이 있으면 엑셀 데이터, 없으면 문자열 값 (AI/DB/사용자입력)
                                            const value = colInfo.mapped && colInfo.index >= 0
                                              ? (rawRow.data as unknown[])[colInfo.index]
                                              : strVal.value || '';
                                            return (
                                              <td key={field.key} className="px-4 py-2 truncate max-w-[250px]">
                                                {strVal.value && !colInfo.mapped ? (
                                                  <span className={
                                                    strVal.type === 'inferred' ? 'text-blue-600 italic' :
                                                    strVal.type === 'db' ? 'text-green-600' :
                                                    strVal.type === 'custom' ? 'text-yellow-600' : ''
                                                  }>{strVal.value}</span>
                                                ) : colInfo.mapped ? (
                                                  String(value ?? '')
                                                ) : (
                                                  <span className="text-gray-300">-</span>
                                                )}
                                              </td>
                                            );
                                          })}
                                        </tr>
                                      ))
                                  ) : (
                                    /* 레거시: sampleData 사용 */
                                    currentSheetInfo.sampleData.map((row, idx) => (
                                      <tr
                                        key={idx}
                                        className={`border-t hover:bg-gray-50 cursor-pointer ${excludedRows.has(idx) ? 'opacity-40' : ''}`}
                                        onClick={() => {
                                          setExcludedRows((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(idx)) {
                                              next.delete(idx);
                                            } else {
                                              next.add(idx);
                                            }
                                            return next;
                                          });
                                        }}
                                      >
                                        <td className="px-2 py-2 text-center w-10">
                                          <input
                                            type="checkbox"
                                            checked={!excludedRows.has(idx)}
                                            readOnly
                                            className="rounded pointer-events-none"
                                          />
                                        </td>
                                        {displayFields.map((field) => {
                                          const colInfo = getColumnInfo(field.key);
                                          const strVal = getStringValue(field.key);
                                          const value = colInfo.mapped ? row[colInfo.name] : strVal.value || '';
                                          return (
                                            <td key={field.key} className="px-4 py-2 truncate max-w-[250px]">
                                              {strVal.value && !colInfo.mapped ? (
                                                <span className={
                                                  strVal.type === 'inferred' ? 'text-blue-600 italic' :
                                                  strVal.type === 'db' ? 'text-green-600' :
                                                  strVal.type === 'custom' ? 'text-yellow-600' : ''
                                                }>{strVal.value}</span>
                                              ) : colInfo.mapped ? (
                                                String(value ?? '')
                                              ) : (
                                                <span className="text-gray-300">-</span>
                                              )}
                                            </td>
                                          );
                                        })}
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                            </>
                          );
                        })()}
                      </div>
                  )}

                  </div>{/* end scrollable content */}

                  {/* AI 추천 사유 - 고정 */}
                  {currentSheetInfo && analysisResult?.aiRecommendation?.reasoning && (
                    <details className="shrink-0 mt-2 border rounded-card">
                      {/* summary는 이 박스의 머리 띠라 컨트롤(10px)이 아니라 부모 박스와 같은 12px로 맞춘다 */}
                      <summary className="px-4 py-2 cursor-pointer text-sm font-medium text-blue-700 bg-blue-50 rounded-card hover:bg-blue-100">
                        AI 분석 사유 보기
                      </summary>
                      <p className="px-4 py-3 text-sm text-gray-700 whitespace-pre-wrap">
                        {analysisResult.aiRecommendation.reasoning}
                      </p>
                    </details>
                  )}

                  {/* 하단: 등록 대상 요약 + 버튼 */}
                  {currentSheetInfo && (() => {
                    // 등록 가능한 파일별 시트 행 수 집계
                    const currentConfigKey = selectedFile ? getSheetConfigKey(selectedFile.name, selectedSheet) : '';
                    const currentConfig: PerSheetConfig = {
                      columnMapping: { ...columnMapping },
                      classificationColumns: [...classificationColumns],
                      excludedRows: new Set(excludedRows),
                      detectedHeaderRows: [...detectedHeaderRows],
                      detectedDataStartRow,
                      addToClassification,
                    };

                    // 파일별로 그룹핑
                    const fileSummaryMap = new Map<string, Array<{ sheetName: string; selectedRows: number }>>();
                    const checkedKeys = new Set<string>();

                    for (const file of selectedFiles) {
                      const analysis = fileAnalysisMap.get(file.name);
                      if (!analysis) continue;
                      const sheetsForFile: Array<{ sheetName: string; selectedRows: number }> = [];

                      for (const sheet of analysis.sheets) {
                        const key = getSheetConfigKey(file.name, sheet.name);
                        const config = key === currentConfigKey ? currentConfig : sheetConfigs.get(key);
                        if (!config) continue;
                        if (!checkSheetCanImport(config) || getMissingColumnFields(config).length > 0) continue;
                        const validCount = getValidRowCount(sheet.name, config, analysis);
                        if (validCount <= 0) continue;
                        sheetsForFile.push({ sheetName: sheet.name, selectedRows: validCount });
                        checkedKeys.add(key);
                      }

                      if (sheetsForFile.length > 0) {
                        fileSummaryMap.set(file.name, sheetsForFile);
                      }
                    }

                    // 현재 시트가 sheetConfigs에 없는 경우 처리
                    if (selectedFile && !checkedKeys.has(currentConfigKey) && checkSheetCanImport(currentConfig) && getMissingColumnFields(currentConfig).length === 0) {
                      const validCount = getValidRowCount(selectedSheet, currentConfig);
                      if (validCount > 0) {
                        const existing = fileSummaryMap.get(selectedFile.name) || [];
                        existing.push({ sheetName: selectedSheet, selectedRows: validCount });
                        fileSummaryMap.set(selectedFile.name, existing);
                      }
                    }

                    const totalSheetCount = Array.from(fileSummaryMap.values()).reduce((sum, sheets) => sum + sheets.length, 0);
                    const totalSelectedRows = Array.from(fileSummaryMap.values()).reduce(
                      (sum, sheets) => sum + sheets.reduce((s, sh) => s + sh.selectedRows, 0), 0
                    );

                    return (
                      <div className="shrink-0 pt-3 border-t space-y-3">
                        {/* 등록 대상 요약 */}
                        {totalSheetCount > 0 && (() => {
                          const allEntries = Array.from(fileSummaryMap.entries()).flatMap(([fileName, sheets]) =>
                            sheets.map(s => ({ fileName, sheetName: s.sheetName, selectedRows: s.selectedRows }))
                          );
                          const first = allEntries[0];
                          const firstLabel = selectedFiles.length > 1
                            ? `${first.fileName.length > 15 ? first.fileName.slice(0, 12) + '...' : first.fileName} / ${first.sheetName} (${first.selectedRows}행)`
                            : `${first.sheetName} (${first.selectedRows}행)`;

                          return (
                            <div className="text-xs relative group/summary inline-block">
                              <span className="text-gray-500 font-medium">등록 대상: </span>
                              <span className="text-gray-700">{firstLabel}</span>
                              {allEntries.length > 1 && (
                                <span className="text-gray-500"> 외 {allEntries.length - 1}건</span>
                              )}
                              <span className="text-gray-400 ml-1">
                                (총 {totalSelectedRows.toLocaleString()}행)
                              </span>
                              {allEntries.length > 1 && (
                                <div className="absolute left-0 bottom-full pb-2 invisible group-hover/summary:visible z-50">
                                  <div className="p-3 bg-gray-900 text-white text-xs rounded-card-sm shadow-xl max-h-48 overflow-y-auto w-96">
                                    <div className="font-medium mb-1.5 text-gray-300">등록 대상 ({allEntries.length}개 시트, 총 {totalSelectedRows.toLocaleString()}행)</div>
                                    {allEntries.map((e, i) => (
                                      <div key={i} className="py-0.5 flex justify-between">
                                        <span className="truncate mr-2">
                                          {selectedFiles.length > 1 ? `${e.fileName.length > 20 ? e.fileName.slice(0, 17) + '...' : e.fileName} / ` : ''}{e.sheetName}
                                        </span>
                                        <span className="shrink-0 text-gray-400">{e.selectedRows}행</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* 버튼 */}
                        <div className="flex justify-end gap-3">
                          <Button variant="secondary" onClick={resetImport}>
                            처음으로
                          </Button>
                          <Button
                            tone="violet"
                            onClick={handleImportAll}
                            disabled={totalSheetCount === 0 || importLoading}
                          >
                            {totalSheetCount > 1
                              ? `등록 (${totalSheetCount}개 시트${selectedFiles.length > 1 ? `, ${fileSummaryMap.size}개 파일` : ''})`
                              : '등록'}
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Step 4: Import 진행 중 */}
              {importStep === 'importing' && (
                <div className="py-8">
                  <h3 className="text-lg font-semibold text-center mb-6">Import 진행 중...</h3>

                  {/* 멀티시트 진행 표시 */}
                  {multiSheetProgress && (
                    <div className="max-w-lg mx-auto mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-indigo-700">
                          시트 {multiSheetProgress.completedSheets + 1}/{multiSheetProgress.totalSheets} 처리 중: &quot;{multiSheetProgress.currentSheetName}&quot;
                        </span>
                      </div>
                      <div className="w-full bg-indigo-100 rounded-full h-2 overflow-hidden mb-3">
                        <div
                          className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${(multiSheetProgress.completedSheets / multiSheetProgress.totalSheets) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* 프로그레스바 (현재 시트) */}
                  <div className="max-w-lg mx-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {importProgress?.currentSheet
                          ? `${importProgress.currentSheet} 시트 처리 중`
                          : '준비 중...'}
                      </span>
                      <span className="text-sm font-bold text-brand-600">
                        {importProgress?.percent ?? 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                      <div
                        className="bg-brand-600 h-4 rounded-full transition-all duration-300"
                        style={{ width: `${importProgress?.percent ?? 0}%` }}
                      />
                    </div>

                    {/* 처리 건수 */}
                    {importProgress && importProgress.total > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm text-gray-500 text-center">
                          {importProgress.processed.toLocaleString()} / {importProgress.total.toLocaleString()} 행 처리됨
                        </p>
                        <div className="flex justify-center gap-6 text-sm">
                          <span className="text-green-600">
                            생성: {importProgress.created.toLocaleString()}
                          </span>
                          <span className="text-blue-600">
                            업데이트: {importProgress.updated.toLocaleString()}
                          </span>
                          <span className="text-yellow-600">
                            건너뜀: {importProgress.skipped.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* 후처리: 임베딩 */}
                    {importProgress?.postProcess === 'embedding' && (
                      <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-center gap-2 text-sm text-indigo-600">
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>
                            임베딩 처리 중...
                            {importProgress.embeddingTotal
                              ? ` (${(importProgress.embeddingProcessed ?? 0).toLocaleString()}/${importProgress.embeddingTotal.toLocaleString()})`
                              : ''}
                          </span>
                        </div>
                        {importProgress.embeddingTotal ? (
                          <div className="max-w-xs mx-auto">
                            <div className="w-full bg-indigo-100 rounded-full h-1.5">
                              <div
                                className="bg-indigo-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${Math.round(((importProgress.embeddingProcessed ?? 0) / importProgress.embeddingTotal) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>

                  {/* 완료된 시트 목록 */}
                  {multiSheetProgress && multiSheetProgress.sheetResults.length > 0 && (
                    <div className="max-w-lg mx-auto mt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">완료된 시트</h4>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {multiSheetProgress.sheetResults.map((r, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs px-3 py-1.5 bg-gray-50 rounded">
                            <span className="font-medium text-gray-700">{selectedFiles.length > 1 ? `${r.fileName} / ${r.sheetName}` : r.sheetName}</span>
                            {r.error ? (
                              <span className="text-red-600">{r.error}</span>
                            ) : (
                              <span className="text-gray-500">
                                생성 {r.created} / 업데이트 {r.updated} / 건너뜀 {r.skipped}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Import 결과 */}
              {importStep === 'result' && importResult && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    {importResult.success ? (
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-green-600">✓</span>
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl text-red-600">!</span>
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-semibold">
                        {importResult.success ? 'Import 완료' : 'Import 완료 (오류 있음)'}
                      </h2>
                      <p className="text-gray-500">대상 {importResult.totalProcessed}건</p>
                    </div>
                  </div>

                  {/* 결과 요약 */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="bg-gray-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-gray-900">
                        {importResult.totalProcessed}
                      </div>
                      <div className="text-sm text-gray-500">대상</div>
                    </div>
                    <div className="bg-green-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{importResult.created}</div>
                      <div className="text-sm text-gray-500">생성됨</div>
                    </div>
                    <div className="bg-blue-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-blue-600">{importResult.updated}</div>
                      <div className="text-sm text-gray-500">업데이트됨</div>
                    </div>
                    <div className="bg-yellow-50 rounded-card p-4 text-center">
                      <div className="text-2xl font-bold text-yellow-600">{importResult.skipped}</div>
                      <div className="text-sm text-gray-500">건너뜀</div>
                    </div>
                  </div>

                  {/* 시트별 상세 */}
                  {importResult.details && importResult.details.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-medium mb-3">시트별 상세</h3>
                      <div className="border rounded-card overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left">시트</th>
                              <th className="px-4 py-2 text-right">대상</th>
                              <th className="px-4 py-2 text-right">생성</th>
                              <th className="px-4 py-2 text-right">업데이트</th>
                              <th className="px-4 py-2 text-right">건너뜀</th>
                            </tr>
                          </thead>
                        </table>
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-sm">
                            <tbody>
                              {importResult.details.map((detail, idx) => (
                                <tr key={idx} className="border-t">
                                  <td className="px-4 py-2">{detail.sheet}</td>
                                  <td className="px-4 py-2 text-right">{detail.processed}</td>
                                  <td className="px-4 py-2 text-right text-green-600">
                                    {detail.created}
                                  </td>
                                  <td className="px-4 py-2 text-right text-blue-600">
                                    {detail.updated}
                                  </td>
                                  <td className="px-4 py-2 text-right text-yellow-600">
                                    {detail.skipped}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 에러 목록 */}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-medium mb-3 text-red-600">
                        오류 ({importResult.errors.length}건)
                      </h3>
                      <div className="bg-red-50 rounded-card p-4 max-h-32 overflow-y-auto">
                        <ul className="text-sm text-red-700 space-y-1">
                          {importResult.errors.slice(0, 10).map((error, idx) => (
                            <li key={idx}>- {error}</li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-red-500">
                              ... 외 {importResult.errors.length - 10}건
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <Button variant="secondary" onClick={resetImport}>
                      새 Import
                    </Button>
                    <Button variant="primary" onClick={closeImportModal}>
                      닫기
                    </Button>
                  </div>
                </>
              )}
            </div>
        </Modal>
      )}

      {/* 법규 매칭 관리 모달 */}
      {matchRegulationsModal && (
        // 종전처럼 매칭·초기화 진행 중에는 바깥 클릭·✕가 안 닫는다(Esc도 같이 막는다).
        <Modal
          open
          onClose={() => setMatchRegulationsModal(false)}
          title="법규 매칭 관리"
          size="lg"
          closeOnOverlay={!(matchRegulationsLoading || resetLegalBasesLoading)}
          closeOnEsc={!(matchRegulationsLoading || resetLegalBasesLoading)}
          showCloseButton={!(matchRegulationsLoading || resetLegalBasesLoading)}
          closeLocked={matchRegulationsLoading || resetLegalBasesLoading}
        >
            <div>

              {/* 현재 상태 */}
              {stats && (
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <div className="bg-gray-50 rounded-card p-3 text-center">
                    <div className="text-xs text-gray-500">전체</div>
                    <div className="text-xl font-bold">{stats.total.toLocaleString()}</div>
                  </div>
                  <div className="bg-teal-50 rounded-card p-3 text-center">
                    <div className="text-xs text-teal-600">매칭 완료</div>
                    <div className="text-xl font-bold text-teal-700">{stats.regulationMatched.toLocaleString()}</div>
                  </div>
                  <div className="bg-orange-50 rounded-card p-3 text-center">
                    <div className="text-xs text-orange-600">미매칭</div>
                    <div className="text-xl font-bold text-orange-700">
                      {(stats.total - stats.regulationMatched).toLocaleString()}
                    </div>
                  </div>
                </div>
              )}

              {/* 매칭 실행 */}
              <div className="border rounded-card p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">법규 매칭 실행</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      {forceRematch
                        ? '모든 항목의 법규를 다시 매칭합니다.'
                        : '미매칭 항목만 대상으로 법규를 매칭합니다.'}
                    </div>
                    <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={forceRematch}
                        onChange={(e) => setForceRematch(e.target.checked)}
                        disabled={matchRegulationsLoading}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-xs text-gray-600">강제 리매칭 (기존 매칭 포함 전체 재실행)</span>
                    </label>
                  </div>
                  <Button
                    tone="teal"
                    size="sm"
                    onClick={handleMatchRegulations}
                    disabled={matchRegulationsLoading || resetLegalBasesLoading}
                    className="shrink-0 ml-4"
                  >
                    {matchRegulationsLoading ? (
                      <>
                        <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full" />
                        매칭 중...
                      </>
                    ) : (
                      '매칭 실행'
                    )}
                  </Button>
                </div>
                {/* 진행도 표시 */}
                {matchProgress && matchProgress.status === 'processing' && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-1.5">
                      <span>
                        {matchProgress.total > 0
                          ? `${matchProgress.processed.toLocaleString()} / ${matchProgress.total.toLocaleString()}건 처리`
                          : '준비 중...'}
                      </span>
                      <span>
                        {matchProgress.total > 0
                          ? `${Math.round((matchProgress.processed / matchProgress.total) * 100)}%`
                          : ''}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-teal-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: matchProgress.total > 0 ? `${Math.round((matchProgress.processed / matchProgress.total) * 100)}%` : '0%' }}
                      />
                    </div>
                    <div className="flex gap-4 mt-1.5 text-xs text-gray-500">
                      <span>매칭: {matchProgress.matched.toLocaleString()}</span>
                      <span>건너뜀: {matchProgress.skipped.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* 법규 리셋 */}
              <div className="border border-red-200 rounded-card p-4 mb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">법규 데이터 초기화</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      모든 공용 지식베이스의 법규 매칭 데이터를 제거합니다.
                    </div>
                  </div>
                  <button
                    onClick={handleResetLegalBases}
                    disabled={matchRegulationsLoading || resetLegalBasesLoading}
                    className="shrink-0 ml-4 px-4 py-2 border border-red-300 text-red-600 rounded-control hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                  >
                    {resetLegalBasesLoading ? (
                      <>
                        <span className="animate-spin inline-block w-3.5 h-3.5 border-2 border-red-400 border-t-transparent rounded-full" />
                        초기화 중...
                      </>
                    ) : (
                      '법규 초기화'
                    )}
                  </button>
                </div>
              </div>

              {/* 미매칭 항목 보기 */}
              <div className="border rounded-card p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">미매칭 항목 보기</div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      법규가 매칭되지 않은 항목만 필터링합니다.
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setLegalBasesFilter('unmatched');
                      setPage(1);
                      setMatchRegulationsModal(false);
                    }}
                    disabled={matchRegulationsLoading}
                    className="shrink-0 ml-4 px-4 py-2 border border-orange-300 text-orange-600 rounded-control hover:bg-orange-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    미매칭 필터
                  </button>
                </div>
              </div>
            </div>
        </Modal>
      )}

      {/* 초기화 확인 모달 */}
      {/*
        일괄 삭제 확인 — 「수」가 아니라 «대상»을 보인다.
        선택이 페이지를 넘어 살아남으므로 목록을 `measures`(현재 페이지)에서 뽑으면 일부가 빠진다.
        `selectedItems`가 선택 시점에 라벨을 함께 들고 있어 그 축이 닫힌다.
      */}
      {bulkDeleteModal && (
        <Modal
          open
          onClose={() => setBulkDeleteModal(false)}
          ariaLabel="선택 항목 삭제"
          size="xl"
          // 삭제 요청 중에는 닫는 길을 전부 막는다(WorkClassifications 일괄 삭제와 같은 형태) —
          // 닫혀도 요청은 계속 돌아 결과 토스트만 맥락 없이 뜬다.
          closeOnOverlay={!bulkDeleteLoading}
          closeOnEsc={!bulkDeleteLoading}
          showCloseButton={!bulkDeleteLoading}
          closeLocked={bulkDeleteLoading}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setBulkDeleteModal(false)}
                disabled={bulkDeleteLoading}
              >
                취소
              </Button>
              <Button variant="danger" onClick={confirmBulkDelete} disabled={bulkDeleteLoading}>
                {bulkDeleteLoading ? '삭제 중...' : `${selectedItems.size}건 삭제`}
              </Button>
            </>
          }
        >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-xl text-red-600">!</span>
                </div>
                <h2 className="text-lg font-bold text-red-600">선택 항목 삭제</h2>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-card p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">
                  이 작업은 되돌릴 수 없습니다. 삭제 기록도 남지 않습니다.
                </p>
                <ul className="text-sm text-red-600 space-y-1">
                  <li>- 지식베이스 {selectedItems.size}건과 그 대책·법규 연결이 함께 삭제됩니다</li>
                  {offPageSelectedCount > 0 && (
                    <li>- 그중 {offPageSelectedCount}건은 다른 페이지에서 선택한 항목입니다</li>
                  )}
                </ul>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">삭제 대상</p>
                <ul className="max-h-64 overflow-y-auto border rounded-card divide-y divide-gray-100">
                  {Array.from(selectedItems.entries()).map(([id, label]) => (
                    <li key={id} className="px-3 py-2 text-sm text-gray-700 truncate" title={label}>
                      {label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
        </Modal>
      )}

      {resetModal && (
        // 비밀번호 칸 첫 포커스 — `autoFocus`는 base가 열림 직후 패널로 포커스를 옮기며 덮으므로
        // `initialFocusRef`로 준다(종전 Overlay에선 autoFocus가 그대로 살았다).
        <Modal
          open
          onClose={() => {
            setResetModal(false);
            setResetPassword('');
          }}
          ariaLabel="지식베이스 초기화"
          size="md"
          initialFocusRef={resetPasswordRef}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setResetModal(false);
                  setResetPassword('');
                }}
              >
                취소
              </Button>
              <Button
                variant="danger"
                onClick={handleResetKnowledgeBase}
                disabled={!resetPassword || resetLoading}
              >
                {resetLoading ? '처리 중...' : '초기화 실행'}
              </Button>
            </>
          }
        >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-xl text-red-600">!</span>
                </div>
                <h2 className="text-lg font-bold text-red-600">지식베이스 초기화</h2>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-card p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">
                  이 작업은 되돌릴 수 없습니다.
                </p>
                <ul className="text-sm text-red-600 space-y-1">
                  <li>- 모든 지식베이스 데이터가 삭제됩니다</li>
                  <li>- 관련 대책, 법규 연결, 매칭 데이터도 함께 삭제됩니다</li>
                  <li>- 현재 {stats?.total.toLocaleString() || 0}건의 데이터가 삭제됩니다</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  본인 확인을 위해 비밀번호를 입력해주세요
                </label>
                <input
                  ref={resetPasswordRef}
                  type="password"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && resetPassword) handleResetKnowledgeBase();
                  }}
                  className="w-full px-3 py-2 border rounded-control focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="슈퍼관리자 비밀번호"
                />
              </div>
            </div>
        </Modal>
      )}
      {confirmDialog}
    </div>
  );
}
